import {Badge} from '@dropbox/dig-components/badge';
import {Button} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, DownloadLine} from '@dropbox/dig-icons/assets';
import {WarningLine} from '@dropbox/dig-icons/assets';
import * as Sentry from '@sentry/react';
import {useQuery} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import cx from 'classnames';
import {
  type EmployeeRoleLevel,
  type RoadmapGroupHierarchyTeam,
  RoadmapNodeRole,
  type RoadmapRollupResponse,
} from 'client';
import type {GroupTab} from 'components/DSYS/GroupTabs';
import {GroupTabs} from 'components/DSYS/GroupTabs';
import {Link} from 'components/DSYS/Link';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import type {SortOption} from 'components/DSYS/TableToolbar';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {differenceInCalendarDays} from 'date-fns';
import {formatDateWithYear as formatCalendarDateWithYear, parseDateInput} from 'helpers/dates';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useEmployee} from 'hooks/useEmployee';
import {useAtomValue} from 'jotai';
import {lazy, type ReactNode, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import type {PersonOption} from 'views/workstreams/detail/PeopleSearch';
import workstreamTableStyles from 'views/workstreams/detail/WorkstreamTable.module.css';

import {
  fetchRoadmapAllocations,
  fetchRoadmapData,
  fetchRoadmapFacets,
  fetchRoadmapLaunches,
  fetchRoadmapNavigation,
  fetchRoadmapRollup,
  type RoadmapAllocationsResponse,
} from './api';
import {useRoadmapAuditFindings} from './audit/useRoadmapAuditFindings';
import {DashboardProgressStrip} from './DashboardProgressStrip';
import {
  buildRoadmapGroupingOptions,
  getRoadmapGroupingMaxDepth,
  getRoadmapHierarchyDepths,
  prependCurrentScopeInitiatives,
  pruneArchivedEmptyRoadmapHierarchy,
  pruneEmptyRoadmapHierarchy,
  resolveRoadmapGrouping,
  sortRoadmapHierarchyItems,
} from './grouping';
import {buildMyRoadmapAllocations} from './myRoadmap';
import {resolveRoadmapNavigation} from './navigation';
import {
  buildRoadmapAuditSearchParams,
  buildRoadmapSearchParams,
  getRoadmapSearchState,
  resolveRoadmapAudience,
  type RoadmapAuditFilters,
} from './queryParams';
import styles from './Roadmap.module.css';
import {
  buildRoadmapDatasetFromItems,
  getCurrentQuarterLabel,
  initiativeMatchesRoadmapQuarter,
  isDoneStatus,
  isOverdue,
  type RoadmapDataset,
  type RoadmapItem,
} from './roadmapData';
import {
  getPersonalizedRoadmapDatasetScope,
  getRoadmapContentQueryKey,
  getRoadmapPersonLookupState,
  shouldLoadUnpersonalizedRoadmapDataset,
} from './roadmapDatasetRequest';
import {RoadmapInitiativeLink} from './RoadmapInitiativeLink';
import {RoadmapJiraStatus} from './RoadmapJiraStatus';
import {RoadmapContentLoadingSkeleton, RoadmapLoadingSkeleton} from './RoadmapLoadingSkeleton';
import {getRoadmapLoadState, isRoadmapSnapshotUnavailableError} from './roadmapLoadState';
import {type RoadmapNodeUpdate, RoadmapNodeUpdateBlock} from './RoadmapNodeUpdateBlock';
import {RoadmapPerson} from './RoadmapPerson';
import {RoadmapQuarterControl} from './RoadmapQuarterControl';
import {RoadmapScopeSidebar} from './RoadmapScopeSidebar';
import {useRoadmapSearchParams} from './roadmapSearchParams';
import {RoadmapSettingsMenu} from './RoadmapSettingsMenu';
import {
  getRoadmapProgressBucket,
  normalizeRoadmapStatus,
  ROADMAP_STATUS_COLORS,
} from './roadmapStatusColors';
import {getRoadmapTabDefinitions} from './roadmapTabs';
import {RoadmapViewTitle} from './RoadmapViewTitle';
import {AllTab, type PeopleRoadmapDashboardData} from './tabs/AllTab';
import {AuditFindingsTab} from './tabs/AuditFindingsTab';
import {OverviewTab} from './tabs/OverviewTab';
import {RoadmapTab as RoadmapTabContent} from './tabs/RoadmapTab';
import {RollupTab} from './tabs/RollupTab';
import {
  findRoadmapTimeframeForQuarter,
  getDefaultQuarterForRoadmapTimeframe,
  getQuartersForRoadmapTimeframes,
  getRequestedOrCurrentRoadmapTimeframeKey,
  getRoadmapRequestQuarter,
} from './timeframeQuarter';
import {
  type AuditInconsistencyIssueType,
  type AuditInvestmentNode,
  type AuditWorkstreamNode,
  type DashboardProgressBucket,
  INVESTMENT_CATEGORY_ORDER,
  INVESTMENT_CATEGORY_SHORT_LABELS,
  type LaunchTableRow,
  ROADMAP_STATUS_ORDER,
  type RoadmapGrouping,
  type RoadmapHierarchyNode,
  type RoadmapSortOrder,
  type RoadmapTab,
} from './types';
import {useRoadmapNodeExpansion} from './useRoadmapNodeExpansion';

const ROADMAP_SORT_OPTIONS: SortOption[] = [
  {id: 'end_date', label: 'End Date'},
  {id: 'status', label: 'Status'},
  {id: 'owner', label: 'DRI'},
];

const ALLOCATION_CATEGORY_COLUMN_MIN_WIDTH = 110;
const INVESTMENT_CATEGORY_COLUMN_MIN_WIDTH = 72;
const INVESTMENT_CATEGORY_HEADER_CHARACTER_WIDTH = 8;
const INVESTMENT_CATEGORY_HEADER_INLINE_PADDING = 16;
const DRI_COLUMN_MIN_WIDTH = 130;
const DRI_COLUMN_WIDTH = 150;
const STATUS_COLUMN_MIN_WIDTH = 125;
const STATUS_COLUMN_WIDTH = 125;
const INITIATIVE_COLUMN_MIN_WIDTH = 280;
const WORKSTREAM_COLUMN_MIN_WIDTH = 280;
const START_DATE_COLUMN_MIN_WIDTH = 118;
const START_DATE_COLUMN_WIDTH = 118;
const END_DATE_COLUMN_MIN_WIDTH = 118;
const END_DATE_COLUMN_WIDTH = 118;
const RESOURCES_COLUMN_MIN_WIDTH = 138;
const RESOURCES_COLUMN_WIDTH = 138;
const PEOPLE_DASHBOARD_ROW_LIMIT = 5;

const DependenciesTab = lazy(() =>
  import('./tabs/DependenciesTab').then(({DependenciesTab: Tab}) => ({default: Tab}))
);

const getRollupDependencyCount = (rollup: RoadmapRollupResponse | undefined): number | null => {
  if (!rollup) return null;
  const rows = rollup.rows ?? [];
  const rowKeys = new Set(rows.map((row) => row.node.key));
  const rootSummaryRows = rows.filter(
    (row) =>
      row.node.semantic_role !== RoadmapNodeRole.ROADMAP_INITIATIVE &&
      (!row.node.parent_key || !rowKeys.has(row.node.parent_key))
  );
  const rowsToTotal =
    rootSummaryRows.length > 0
      ? rootSummaryRows
      : rows.filter((row) => row.node.semantic_role === RoadmapNodeRole.ROADMAP_INITIATIVE);
  return rowsToTotal.reduce(
    (total, row) => total + (row.metrics.structural.dependency_count ?? 0),
    0
  );
};

const formatDateWithYear = (value: string | null) => formatCalendarDateWithYear(value) || 'TBD';

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const formatDecimal = (value: number) => {
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
};

const getInvestmentCategoryColumnWidth = (label: string) =>
  Math.max(
    INVESTMENT_CATEGORY_COLUMN_MIN_WIDTH,
    label.length * INVESTMENT_CATEGORY_HEADER_CHARACTER_WIDTH +
      INVESTMENT_CATEGORY_HEADER_INLINE_PADDING
  );

const escapeCsvField = (value: string | null | undefined): string => {
  if (value == null) return '';
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const normalizeQuarterLabel = (quarter: string | null | undefined) => {
  const trimmed = quarter?.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{4})[\s-]?Q(\d)$/i);
  if (!match) return null;
  return `${match[1]} Q${match[2]}`;
};

const getDashboardWorkstreamName = (item: RoadmapItem) =>
  item.ancestry[item.ancestry.length - 1]?.title ?? item.parentKey ?? '—';

const getRoadmapOwner = (item: RoadmapItem): EmployeeRoleLevel | null => {
  const ldap = item.assigneeEmail?.split('@')[0]?.trim().toLowerCase() ?? null;
  const name = item.assigneeName?.trim() ?? null;
  if (!ldap || !name) return null;

  return {
    ldap,
    name,
    profile_photo_url: null,
  } as unknown as EmployeeRoleLevel;
};

type GanttTone =
  | 'done'
  | 'onTrack'
  | 'atRisk'
  | 'blocked'
  | 'overdue'
  | 'cancelled'
  | 'notStarted'
  | 'closed';

const GANTT_TONE_COLORS: Record<GanttTone, string> = {
  done: ROADMAP_STATUS_COLORS.completed,
  onTrack: ROADMAP_STATUS_COLORS.onTrack,
  atRisk: ROADMAP_STATUS_COLORS.atRiskDelayed,
  blocked: ROADMAP_STATUS_COLORS.atRiskDelayed,
  overdue: 'var(--dig-color__alert__base)',
  cancelled: ROADMAP_STATUS_COLORS.cancelled,
  notStarted: ROADMAP_STATUS_COLORS.notStarted,
  closed: ROADMAP_STATUS_COLORS.cancelled,
};

const GANTT_BAR_DATE_TEXT_COLOR = '#fff';

const getQuarterDateRange = (quarter: string) => {
  const [yearText, quarterText] = quarter.split(' Q');
  const year = Number(yearText);
  const quarterNumber = Number(quarterText);
  const startMonth = (quarterNumber - 1) * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
  return {start, end};
};

const getAuditIssueLabel = (issueType: AuditInconsistencyIssueType) => {
  switch (issueType) {
    case 'past_end_date':
      return 'Past end date';
    case 'past_start_date':
      return 'Past start date';
    case 'missing_end_date':
      return 'Missing end date';
    default:
      return issueType;
  }
};

const getInitiativeAuditIssueTypes = (initiative: RoadmapItem): AuditInconsistencyIssueType[] => {
  const warningIssueTypes: AuditInconsistencyIssueType[] = [];

  if (initiative.warnings.pastEndDate) {
    warningIssueTypes.push('past_end_date');
  }
  if (initiative.warnings.pastStartDate) {
    warningIssueTypes.push('past_start_date');
  }
  if (initiative.warnings.missingEndDate) {
    warningIssueTypes.push('missing_end_date');
  }

  if (warningIssueTypes.length > 0) {
    return warningIssueTypes;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const issueTypes: AuditInconsistencyIssueType[] = [];

  if (initiative.endDate && !isDoneStatus(initiative.status)) {
    const endDate = parseDateInput(initiative.endDate);
    if (!Number.isNaN(endDate.getTime()) && endDate < today) {
      issueTypes.push('past_end_date');
    }
  }

  if (initiative.startDate && ['Backlog', 'To Do'].includes(initiative.status ?? '')) {
    const startDate = parseDateInput(initiative.startDate);
    if (!Number.isNaN(startDate.getTime()) && startDate < today) {
      issueTypes.push('past_start_date');
    }
  }

  const normalizedStatus = initiative.status?.toLowerCase() ?? '';
  if (
    (normalizedStatus === 'in progress' ||
      normalizedStatus === 'at risk' ||
      normalizedStatus === 'at  risk') &&
    !initiative.endDate
  ) {
    issueTypes.push('missing_end_date');
  }

  return issueTypes;
};

const quarterToDate = (quarter: string | null) => {
  if (!quarter) return null;
  const [yearText, quarterText] = quarter.split(' Q');
  const year = Number(yearText);
  const quarterNumber = Number(quarterText);
  const month = (quarterNumber - 1) * 3;
  return new Date(year, month + 2, 28);
};

const groupHierarchyItemsByParent = (items: RoadmapItem[]) => {
  const childrenByParent = new Map<string, RoadmapItem[]>();
  items.forEach((item) => {
    const parentKey = item.parentKey ?? '__root__';
    const existing = childrenByParent.get(parentKey) ?? [];
    existing.push(item);
    childrenByParent.set(parentKey, existing);
  });
  return childrenByParent;
};

const generateGanttTimelineLabels = (startDate: Date, endDate: Date) => {
  const months: {name: string; startOffset: number; width: number}[] = [];
  const weeks: {label: string; startOffset: number; width: number}[] = [];

  const totalDays = differenceInCalendarDays(endDate, startDate) + 1;

  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const monthName = currentDate.toLocaleDateString('en-US', {month: 'short'});
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const visibleStart = monthStart < startDate ? startDate : monthStart;
    const visibleEnd = monthEnd > endDate ? endDate : monthEnd;

    const startOffset = differenceInCalendarDays(visibleStart, startDate);
    const daysInMonth = differenceInCalendarDays(visibleEnd, visibleStart) + 1;

    months.push({
      name: monthName,
      startOffset: (startOffset / totalDays) * 100,
      width: (daysInMonth / totalDays) * 100,
    });

    let weekStart = new Date(visibleStart);
    while (weekStart <= visibleEnd) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const actualEnd = weekEnd > visibleEnd ? visibleEnd : weekEnd;

      const weekStartOffset = differenceInCalendarDays(weekStart, startDate);
      const weekDays = differenceInCalendarDays(actualEnd, weekStart) + 1;

      weeks.push({
        label: `${weekStart.getMonth() + 1}/${weekStart.getDate()}`,
        startOffset: (weekStartOffset / totalDays) * 100,
        width: (weekDays / totalDays) * 100,
      });

      weekStart = new Date(actualEnd);
      weekStart.setDate(weekStart.getDate() + 1);
    }

    currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  }

  return {months, weeks, totalDays};
};

const calculateGanttBarPosition = (
  initiative: RoadmapItem,
  quarterStart: Date,
  quarterEnd: Date,
  totalDays: number
): {
  left: number;
  width: number;
  isDiamond: boolean;
  startLabel: string | null;
  endLabel: string;
} | null => {
  if (!initiative.endDate) return null;

  const endDate = parseDateInput(initiative.endDate);
  const startDate = initiative.startDate ? parseDateInput(initiative.startDate) : null;
  if (Number.isNaN(endDate.getTime()) || (startDate && Number.isNaN(startDate.getTime()))) {
    return null;
  }

  if (endDate < quarterStart) return null;

  const formatLabelDate = (date: Date) => `${date.getMonth() + 1}/${date.getDate()}`;
  const visibleLabelDate = endDate > quarterEnd ? quarterEnd : endDate;
  const endLabel = formatLabelDate(visibleLabelDate);

  if (!startDate) {
    const dayOffset = differenceInCalendarDays(endDate, quarterStart);
    const clampedOffset = Math.min(Math.max(0, dayOffset), totalDays);
    return {
      left: (clampedOffset / totalDays) * 100,
      width: 0,
      isDiamond: true,
      endLabel,
      startLabel: null,
    };
  }

  const visibleStart = startDate < quarterStart ? quarterStart : startDate;
  const visibleEnd = endDate > quarterEnd ? quarterEnd : endDate;

  const startOffset = differenceInCalendarDays(visibleStart, quarterStart);
  const endOffset = differenceInCalendarDays(visibleEnd, quarterStart);
  const barWidth = Math.max(1, endOffset - startOffset);

  return {
    left: (startOffset / totalDays) * 100,
    width: (barWidth / totalDays) * 100,
    isDiamond: false,
    startLabel: formatLabelDate(visibleStart),
    endLabel,
  };
};

const getGanttTone = (status: string | null, isOverdueDate: boolean): GanttTone => {
  const normalized = status?.trim();
  if (normalized === 'Done' || normalized === 'Completed') return 'done';
  if (isOverdueDate) return 'overdue';
  if (normalized === 'In Progress' || normalized === 'On Track') return 'onTrack';
  if (normalized === 'At Risk' || normalized === 'At  Risk') return 'atRisk';
  if (normalized === 'Delayed' || normalized === 'Blocked' || normalized === 'On Hold') {
    return 'blocked';
  }
  if (normalized === 'Cancelled') return 'cancelled';
  return 'notStarted';
};

const getGanttToneColor = (tone: GanttTone) => GANTT_TONE_COLORS[tone];

const getDerivedEnd = (item: RoadmapItem, selectedQuarter: string) =>
  item.endDate
    ? parseDateInput(item.endDate)
    : (quarterToDate(item.deliveryQuarter) ?? getQuarterDateRange(selectedQuarter).end);

type AllocationWorkstream = RoadmapAllocationsResponse['workstreams'][number];

const sumAllocationCategories = (
  left: Record<string, number>,
  right: Record<string, number>
): Record<string, number> => {
  const next = {...left};
  Object.entries(right).forEach(([category, value]) => {
    next[category] = (next[category] ?? 0) + value;
  });
  return next;
};

const resolveAllocationHierarchyGroup = (
  workstream: AllocationWorkstream,
  dataset: RoadmapDataset | undefined
) => {
  if (!dataset) {
    return {key: workstream.jira_key, title: workstream.title};
  }

  const roadmapNode = dataset.itemsByKey.get(workstream.jira_key);
  if (!roadmapNode) {
    return {key: workstream.jira_key, title: workstream.title};
  }

  const ancestryItems = roadmapNode.ancestorKeys
    .map((key) => dataset.itemsByKey.get(key))
    .filter((item): item is RoadmapItem => Boolean(item));
  const topLevelPeer = ancestryItems.find((item) => item.issueType === roadmapNode.issueType);

  if (topLevelPeer) return {key: topLevelPeer.key, title: topLevelPeer.title};
  return {key: roadmapNode.key, title: roadmapNode.title};
};

const aggregateAllocationsByHierarchy = (
  workstreams: AllocationWorkstream[] | undefined,
  dataset: RoadmapDataset | undefined
): AllocationWorkstream[] => {
  if (!workstreams) return [];

  const grouped = new Map<string, AllocationWorkstream>();

  workstreams.forEach((workstream) => {
    const group = resolveAllocationHierarchyGroup(workstream, dataset);
    const existing = grouped.get(group.key);

    const devEstimates = {
      q1: workstream.dev_estimates?.q1 ?? 0,
      q2: workstream.dev_estimates?.q2 ?? 0,
      q3: workstream.dev_estimates?.q3 ?? 0,
      q4: workstream.dev_estimates?.q4 ?? 0,
    };
    const categories = workstream.investment_categories ?? {};

    if (!existing) {
      grouped.set(group.key, {
        jira_key: group.key,
        title: group.title,
        initiative_count: workstream.initiative_count,
        total_headcount: workstream.total_headcount ?? 0,
        investment_categories: {...categories},
        dev_estimates: devEstimates,
      });
      return;
    }

    grouped.set(group.key, {
      ...existing,
      title: group.title,
      initiative_count: existing.initiative_count + workstream.initiative_count,
      total_headcount: (existing.total_headcount ?? 0) + (workstream.total_headcount ?? 0),
      investment_categories: sumAllocationCategories(
        existing.investment_categories ?? {},
        categories
      ),
      dev_estimates: {
        q1: (existing.dev_estimates?.q1 ?? 0) + devEstimates.q1,
        q2: (existing.dev_estimates?.q2 ?? 0) + devEstimates.q2,
        q3: (existing.dev_estimates?.q3 ?? 0) + devEstimates.q3,
        q4: (existing.dev_estimates?.q4 ?? 0) + devEstimates.q4,
      },
    });
  });

  return [...grouped.values()].sort((left, right) =>
    left.title.localeCompare(right.title, undefined, {numeric: true})
  );
};

const compareItems = (
  left: RoadmapItem,
  right: RoadmapItem,
  sortOrder: RoadmapSortOrder,
  selectedQuarter: string
) => {
  if (sortOrder === 'owner') {
    return (left.assigneeName ?? 'ZZZ').localeCompare(right.assigneeName ?? 'ZZZ');
  }

  if (sortOrder === 'status') {
    const leftIndex = ROADMAP_STATUS_ORDER.indexOf(
      left.status as (typeof ROADMAP_STATUS_ORDER)[number]
    );
    const rightIndex = ROADMAP_STATUS_ORDER.indexOf(
      right.status as (typeof ROADMAP_STATUS_ORDER)[number]
    );
    const safeLeftIndex = leftIndex === -1 ? ROADMAP_STATUS_ORDER.length : leftIndex;
    const safeRightIndex = rightIndex === -1 ? ROADMAP_STATUS_ORDER.length : rightIndex;

    if (safeLeftIndex !== safeRightIndex) {
      return safeLeftIndex - safeRightIndex;
    }
  }

  const leftEnd = getDerivedEnd(left, selectedQuarter).getTime();
  const rightEnd = getDerivedEnd(right, selectedQuarter).getTime();
  if (leftEnd !== rightEnd) {
    return leftEnd - rightEnd;
  }

  return left.title.localeCompare(right.title);
};

const RoadmapWorkstreamName = ({item, lines = 1}: {item: RoadmapItem; lines?: 1 | 2}) => {
  const name = getDashboardWorkstreamName(item);
  const content = (
    <Truncate lines={lines} tooltipControlProps={{placement: 'top-start'}}>
      {name}
    </Truncate>
  );

  return (
    <Text
      size="small"
      className={cx(
        workstreamTableStyles.workstreamTitleText,
        styles.roadmapWorkstreamTitle,
        lines === 2 ? styles.multilineLabelWithKey : styles.inlineLabelWithKey
      )}
    >
      {item.workstreamId != null ? (
        <Link
          to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(item.workstreamId))}
          aria-label={`View workstream: ${name}`}
          hasNoUnderline
          monochromatic
          showUnderlineOnHover
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </Text>
  );
};

const RoadmapMetaDate = ({
  date,
  isOverdueDate = false,
}: {
  date: string | null;
  isOverdueDate?: boolean;
}) => (
  <Text
    size="small"
    color="subtle"
    className={cx(styles.roadmapMetaText, {[styles.overdueDate]: isOverdueDate})}
  >
    {formatDateWithYear(date)}
  </Text>
);

const RoadmapDriCell = ({item}: {item: RoadmapItem}) => {
  const owner = getRoadmapOwner(item);

  if (!owner) {
    return (
      <Text size="small" color="subtle" className={styles.roadmapMetaText}>
        —
      </Text>
    );
  }

  return (
    <div className={styles.roadmapDriCell}>
      <RoadmapPerson person={owner} />
    </div>
  );
};

const DashboardDocumentLinks = ({item}: {item: RoadmapItem}) => {
  const directLinks = [
    {label: 'PRD', url: item.links.find((link) => link.label === 'PRD')?.url ?? null},
    {label: 'UX', url: item.links.find((link) => link.label === 'UX')?.url ?? null},
    {label: 'Tech', url: item.links.find((link) => link.label === 'Tech spec')?.url ?? null},
  ].filter((link) => Boolean(link.url));

  if (directLinks.length === 0) {
    return (
      <Text size="small" color="subtle" className={styles.roadmapMetaText}>
        —
      </Text>
    );
  }

  return (
    <div className={styles.dashboardLinkList}>
      {directLinks.map((link, index) => (
        <span key={`${item.key}-${link.label}`} className={styles.dashboardLinkSlot}>
          {link.url ? (
            <Link
              to={link.url}
              target="_blank"
              hasNoUnderline
              showUnderlineOnHover
              monochromatic
              className={styles.roadmapMetaLink}
            >
              <Text size="small" color="subtle" className={styles.roadmapMetaText}>
                {link.label}
              </Text>
            </Link>
          ) : null}
          {index < directLinks.length - 1 ? (
            <span className={styles.dashboardLinkDivider}>|</span>
          ) : null}
        </span>
      ))}
    </div>
  );
};

const getRoadmapHeaderTone = (issueType: string) => {
  switch (issueType) {
    case 'Narrative Workstream':
      return styles.roadmapHeaderNarrative;
    case 'Sub-Workstream':
      return styles.roadmapHeaderSubworkstream;
    default:
      return undefined;
  }
};

const EMPTY_ROADMAP_NODE_UPDATES: ReadonlyMap<number, RoadmapNodeUpdate> = new Map();

const isRoadmapWorkstreamNode = (issueType: string) =>
  issueType === 'Narrative Workstream' || issueType === 'Sub-Workstream';

const getRoadmapNodeUpdate = (
  node: RoadmapHierarchyNode,
  roadmapNodeUpdatesByWorkstreamId: ReadonlyMap<number, RoadmapNodeUpdate>
): RoadmapNodeUpdate | null => {
  if (!isRoadmapWorkstreamNode(node.issueType)) return null;
  if (!node.workstreamId) return null;
  return roadmapNodeUpdatesByWorkstreamId.get(node.workstreamId) ?? null;
};

const RoadmapInitiativeRow = ({
  initiative,
  depth,
  initiativeAuditIssueTypes,
}: {
  initiative: RoadmapItem;
  depth: number;
  initiativeAuditIssueTypes: AuditInconsistencyIssueType[];
}) => {
  const indent = (depth + 1) * 20;
  const statusNorm = normalizeRoadmapStatus(initiative.status)?.toLowerCase() ?? '';
  const isPastEndDate = initiativeAuditIssueTypes.includes('past_end_date');
  const isDone = statusNorm.includes('done') || statusNorm.includes('complete');
  const initiativeAuditIssueSummary = initiativeAuditIssueTypes
    .map((issueType) => getAuditIssueLabel(issueType))
    .join(', ');
  const hasPastStartDateIssue = initiativeAuditIssueTypes.includes('past_start_date');

  return (
    <Table.Row>
      <Table.Cell style={{paddingLeft: `${12 + indent}px`}}>
        <div className={styles.roadmapInitiativeCell}>
          <span className={styles.roadmapIndentSpacer} />
          {initiativeAuditIssueSummary ? (
            <Tooltip title={initiativeAuditIssueSummary}>
              <Box style={{marginTop: 1}}>
                <UIIcon src={WarningLine} className={atoms({color: 'Border Base'})} size="small" />
              </Box>
            </Tooltip>
          ) : null}
          <RoadmapInitiativeLink title={initiative.title} jiraKey={initiative.key} />
        </div>
      </Table.Cell>
      <Table.Cell>
        <RoadmapDriCell item={initiative} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapJiraStatus status={initiative.status} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapMetaDate date={initiative.startDate} isOverdueDate={hasPastStartDateIssue} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapMetaDate date={initiative.endDate} isOverdueDate={isPastEndDate && !isDone} />
      </Table.Cell>
      <Table.Cell>
        <DashboardDocumentLinks item={initiative} />
      </Table.Cell>
    </Table.Row>
  );
};

const RoadmapHierarchyRows = ({
  node,
  depth,
  collapsedNodes,
  onToggleNode,
  roadmapNodeUpdatesByWorkstreamId,
}: {
  node: RoadmapHierarchyNode;
  depth: number;
  collapsedNodes: ReadonlySet<string>;
  onToggleNode: (key: string) => void;
  roadmapNodeUpdatesByWorkstreamId: ReadonlyMap<number, RoadmapNodeUpdate>;
}) => {
  const isExpanded = !collapsedNodes.has(node.key);
  const hasChildren = node.children.length > 0 || node.initiatives.length > 0;
  const indent = depth * 20;
  const nodeUpdate = getRoadmapNodeUpdate(node, roadmapNodeUpdatesByWorkstreamId);
  const workstreamUrl =
    node.workstreamId && String(node.workstreamId).length > 0
      ? ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(node.workstreamId))
      : null;
  const jiraUrl =
    node.key && String(node.key).length > 0
      ? `https://dropbox.atlassian.net/browse/${node.key}`
      : null;

  return (
    <>
      <Table.Row
        className={cx(workstreamTableStyles.groupHeaderRow, getRoadmapHeaderTone(node.issueType))}
      >
        <Table.Cell
          className={cx(workstreamTableStyles.groupHeaderCell, styles.roadmapGroupHeaderCell)}
          style={{gridColumn: '1 / -1', padding: 0}}
        >
          <div
            className={cx(
              workstreamTableStyles.groupHeaderContent,
              styles.groupHeaderButton,
              styles.roadmapGroupHeaderContent
            )}
            style={{padding: '6px 8px', paddingLeft: `${8 + indent}px`}}
          >
            <RoadmapNodeUpdateBlock label={node.title} update={nodeUpdate}>
              <div className={styles.roadmapHierarchyCell}>
                <span className={styles.groupHeaderToggleIcon}>
                  {hasChildren ? (
                    <button
                      type="button"
                      className={styles.roadmapGroupHeaderToggleButton}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                      aria-expanded={isExpanded}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleNode(node.key);
                      }}
                    >
                      <UIIcon
                        src={isExpanded ? ChevronDownLine : ChevronRightLine}
                        size="small"
                        className={styles.roadmapChevron}
                      />
                    </button>
                  ) : (
                    <span className={styles.roadmapIndentSpacer} />
                  )}
                </span>
                <Text
                  isBold
                  size="small"
                  className={cx(styles.tableTitle, styles.roadmapHierarchyTitle)}
                  title={node.title}
                >
                  {workstreamUrl ? (
                    <Link
                      to={workstreamUrl}
                      openInNewTab
                      hasNoUnderline
                      monochromatic
                      showUnderlineOnHover
                    >
                      {node.title}
                    </Link>
                  ) : (
                    node.title
                  )}
                </Text>
                <Text size="small" color="subtle" className={styles.roadmapHierarchyKey}>
                  (
                  {jiraUrl ? (
                    <Link
                      to={jiraUrl}
                      openInNewTab
                      hasNoUnderline
                      monochromatic
                      showUnderlineOnHover
                    >
                      {node.key}
                    </Link>
                  ) : (
                    node.key
                  )}
                  )
                </Text>
              </div>
            </RoadmapNodeUpdateBlock>
          </div>
        </Table.Cell>
      </Table.Row>

      {isExpanded ? (
        <>
          {node.children.map((child) => (
            <RoadmapHierarchyRows
              key={child.key}
              node={child}
              depth={depth + 1}
              collapsedNodes={collapsedNodes}
              onToggleNode={onToggleNode}
              roadmapNodeUpdatesByWorkstreamId={roadmapNodeUpdatesByWorkstreamId}
            />
          ))}

          {node.initiatives.map((initiative) => (
            <RoadmapInitiativeRow
              key={initiative.key}
              initiative={initiative}
              depth={depth}
              initiativeAuditIssueTypes={getInitiativeAuditIssueTypes(initiative)}
            />
          ))}
        </>
      ) : null}
    </>
  );
};

const RoadmapGanttInitiativeRow = ({
  initiative,
  depth,
  totalDays,
  quarterStart,
  quarterEnd,
  today,
}: {
  initiative: RoadmapItem;
  depth: number;
  totalDays: number;
  quarterStart: Date;
  quarterEnd: Date;
  today: Date;
}) => {
  const indent = depth * 20;
  const isOverdueDate = isOverdue(initiative, today);
  const barPosition = calculateGanttBarPosition(initiative, quarterStart, quarterEnd, totalDays);
  const barTone = getGanttTone(initiative.status, isOverdueDate);
  const barColor = getGanttToneColor(barTone);
  const todayOffset = differenceInCalendarDays(today, quarterStart);
  const showTodayLine = todayOffset >= 0 && todayOffset <= totalDays;
  const shouldRenderStartLabelInBar =
    Boolean(barPosition?.startLabel) &&
    (Boolean(barPosition && barPosition.width > 16) ||
      Boolean(barPosition && barPosition.left <= 5 && barPosition.width > 12));
  const shouldRenderStartLabelOutside =
    Boolean(barPosition?.startLabel) &&
    Boolean(barPosition && barPosition.width <= 16 && !shouldRenderStartLabelInBar);

  return (
    <div className={styles.ganttInitiativeRow} style={{paddingLeft: `${40 + indent}px`}}>
      <RoadmapInitiativeLink
        title={initiative.title}
        jiraKey={initiative.key}
        lines={1}
        className={styles.ganttInitiativeLabel}
      />

      <div className={styles.ganttTrack}>
        {Array.from({length: Math.ceil(totalDays / 7)}).map((_, index) => (
          <div
            key={index}
            className={styles.ganttWeekLine}
            style={{left: `${((index * 7) / totalDays) * 100}%`}}
          />
        ))}

        {showTodayLine ? (
          <div
            className={styles.ganttTodayLine}
            style={{left: `${(todayOffset / totalDays) * 100}%`}}
          />
        ) : null}

        {barPosition ? (
          <>
            {barPosition.isDiamond ? (
              <div className={styles.ganttDiamondMarker} style={{left: `${barPosition.left}%`}}>
                <span className={styles.ganttDiamond} style={{backgroundColor: barColor}} />
                <span className={styles.ganttInlineDateLabel}>{barPosition.endLabel}</span>
              </div>
            ) : (
              <>
                <div
                  className={styles.ganttBar}
                  style={{
                    backgroundColor: barColor,
                    left: `${barPosition.left}%`,
                    width: `${Math.max(barPosition.width, 2)}%`,
                  }}
                >
                  {barPosition.width > 8 ? (
                    <>
                      {barPosition.startLabel && shouldRenderStartLabelInBar ? (
                        <span
                          className={styles.ganttBarStartLabel}
                          style={{color: GANTT_BAR_DATE_TEXT_COLOR}}
                        >
                          {barPosition.startLabel}
                        </span>
                      ) : null}
                      <span
                        className={styles.ganttBarLabel}
                        style={{color: GANTT_BAR_DATE_TEXT_COLOR}}
                      >
                        {barPosition.endLabel}
                      </span>
                    </>
                  ) : null}
                </div>
                {barPosition.startLabel && shouldRenderStartLabelOutside ? (
                  <span
                    className={styles.ganttOutsideStartLabel}
                    style={{left: `${Math.max(barPosition.left - 5, 0)}%`}}
                  >
                    {barPosition.startLabel}
                  </span>
                ) : null}
                {barPosition.width <= 8 ? (
                  <span
                    className={styles.ganttOutsideDateLabel}
                    style={{left: `${barPosition.left + barPosition.width + 1}%`}}
                  >
                    {barPosition.endLabel}
                  </span>
                ) : null}
              </>
            )}
          </>
        ) : (
          <div className={styles.ganttNoDate}>No completion date</div>
        )}
      </div>
    </div>
  );
};

const RoadmapGanttSection = ({
  node,
  depth,
  collapsedNodes,
  onToggleNode,
  roadmapNodeUpdatesByWorkstreamId,
  months,
  weeks,
  totalDays,
  quarterStart,
  quarterEnd,
  today,
}: {
  node: RoadmapHierarchyNode;
  depth: number;
  collapsedNodes: ReadonlySet<string>;
  onToggleNode: (key: string) => void;
  roadmapNodeUpdatesByWorkstreamId: ReadonlyMap<number, RoadmapNodeUpdate>;
  months: {name: string; startOffset: number; width: number}[];
  weeks: {label: string; startOffset: number; width: number}[];
  totalDays: number;
  quarterStart: Date;
  quarterEnd: Date;
  today: Date;
}) => {
  const isExpanded = !collapsedNodes.has(node.key);
  const hasChildren = node.children.length > 0 || node.initiatives.length > 0;
  const indent = depth * 20;
  const todayOffset = differenceInCalendarDays(today, quarterStart);
  const showTodayLine = todayOffset >= 0 && todayOffset <= totalDays;
  const nodeUpdate = getRoadmapNodeUpdate(node, roadmapNodeUpdatesByWorkstreamId);

  return (
    <div className={styles.ganttSection}>
      <div
        className={cx(styles.ganttSectionHeader, getRoadmapHeaderTone(node.issueType))}
        style={{paddingLeft: `${16 + indent}px`}}
      >
        <div className={styles.ganttSectionHeaderContent}>
          <RoadmapNodeUpdateBlock label={node.title} update={nodeUpdate}>
            {hasChildren ? (
              <button
                type="button"
                className={cx(styles.ganttSectionTitleRow, styles.ganttSectionTitleButton)}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                aria-expanded={isExpanded}
                onClick={() => onToggleNode(node.key)}
              >
                <UIIcon
                  src={isExpanded ? ChevronDownLine : ChevronRightLine}
                  size="small"
                  className={styles.roadmapChevron}
                />
                <span
                  className={cx(styles.ganttSectionTitle, styles.roadmapHierarchyTitle)}
                  title={node.title}
                >
                  {node.title}
                </span>
                <span className={styles.ganttSectionKey}>({node.key})</span>
              </button>
            ) : (
              <div className={styles.ganttSectionTitleRow}>
                <span className={styles.roadmapIndentSpacer} />
                <span
                  className={cx(styles.ganttSectionTitle, styles.roadmapHierarchyTitle)}
                  title={node.title}
                >
                  {node.title}
                </span>
                <span className={styles.ganttSectionKey}>({node.key})</span>
              </div>
            )}
          </RoadmapNodeUpdateBlock>
        </div>
      </div>

      {isExpanded ? (
        <>
          {node.initiatives.length > 0 ? (
            <div className={styles.ganttTimelineHeader} style={{paddingLeft: `${40 + indent}px`}}>
              <div className={styles.ganttTimelineLabel}>Initiative</div>
              <div className={styles.ganttTimelineAxis}>
                {months.map((month) => (
                  <div
                    key={`${node.key}-${month.name}-${month.startOffset}`}
                    className={styles.ganttMonthLabel}
                    style={{left: `${month.startOffset}%`, width: `${month.width}%`}}
                  >
                    {month.name}
                  </div>
                ))}
                {weeks.map((week) => (
                  <div
                    key={`${node.key}-${week.label}-${week.startOffset}`}
                    className={styles.ganttWeekLabel}
                    style={{left: `${week.startOffset}%`}}
                  >
                    {week.width > 6 ? week.label : ''}
                  </div>
                ))}
                {showTodayLine ? (
                  <div
                    className={styles.ganttTodayHeaderLine}
                    style={{left: `${(todayOffset / totalDays) * 100}%`}}
                  >
                    <span className={styles.ganttTodayHeaderLabel}>Today</span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {node.initiatives.map((initiative) => (
            <RoadmapGanttInitiativeRow
              key={initiative.key}
              initiative={initiative}
              depth={depth}
              totalDays={totalDays}
              quarterStart={quarterStart}
              quarterEnd={quarterEnd}
              today={today}
            />
          ))}

          {node.children.map((child) => (
            <RoadmapGanttSection
              key={child.key}
              node={child}
              depth={depth + 1}
              collapsedNodes={collapsedNodes}
              onToggleNode={onToggleNode}
              roadmapNodeUpdatesByWorkstreamId={roadmapNodeUpdatesByWorkstreamId}
              months={months}
              weeks={weeks}
              totalDays={totalDays}
              quarterStart={quarterStart}
              quarterEnd={quarterEnd}
              today={today}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};

const AuditSection = ({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <div className={styles.auditSection}>
    <div className={styles.auditSectionHeading}>
      <div className={styles.auditSectionHeadingTitle}>
        <Title size={20}>{title}</Title>
        {typeof count === 'number' ? <Badge size="xsmall">{count}</Badge> : null}
      </div>
      {action ? <div className={styles.auditSectionHeadingAction}>{action}</div> : null}
    </div>
    <div className={styles.auditSectionBody}>{children}</div>
  </div>
);

const AuditInvestmentMetric = ({
  value,
  total,
  label,
}: {
  value: number;
  total: number;
  label: string;
}) => {
  if (value <= 0) {
    return (
      <Text size="small" color="subtle" className={styles.auditMetricText}>
        —
      </Text>
    );
  }

  const percent = total > 0 ? value / total : 0;

  return (
    <Tooltip title={`${label}: ${formatDecimal(value)} dev weeks`}>
      <Text size="small" className={styles.auditMetricText}>
        {`${Math.round(percent * 100)}%`}
      </Text>
    </Tooltip>
  );
};

const AuditInvestmentTotalMetric = ({total}: {total: number}) => (
  <Tooltip title={`Total: ${formatDecimal(total)} dev weeks`}>
    <Text size="small" className={styles.auditMetricText}>
      {formatDecimal(total)}
    </Text>
  </Tooltip>
);

const getAuditNodeTotalDevWeeks = (node: AuditWorkstreamNode): number =>
  node.devWeeks + node.children.reduce((sum, child) => sum + getAuditNodeTotalDevWeeks(child), 0);

const getAuditNodeTotalEstHc = (node: AuditWorkstreamNode): number =>
  node.totalEstHc + node.children.reduce((sum, child) => sum + getAuditNodeTotalEstHc(child), 0);

const flattenAuditWorkstreamNodes = (nodes: AuditWorkstreamNode[]): AuditWorkstreamNode[] =>
  nodes.flatMap((node) => [node, ...flattenAuditWorkstreamNodes(node.children)]);

const flattenAuditInvestmentNodes = (nodes: AuditInvestmentNode[]): AuditInvestmentNode[] =>
  nodes.flatMap((node) => [node, ...flattenAuditInvestmentNodes(node.children)]);

const buildHeadcountCsv = (rows: AuditWorkstreamNode[], totalDevWeeks: number, totalHc: number) => {
  const header = ['Workstream', 'Dev Weeks', 'Total Est HC', 'Allocation'];

  const csvRows = rows.map((node) => {
    const isGroup = node.children.length > 0;
    const displayDevWeeks = isGroup ? getAuditNodeTotalDevWeeks(node) : node.devWeeks;
    const displayHc = isGroup ? getAuditNodeTotalEstHc(node) : node.totalEstHc;
    const displayAllocation = totalHc > 0 ? displayHc / totalHc : 0;

    return [
      escapeCsvField(`${node.title} (${node.key})`),
      escapeCsvField(formatDecimal(displayDevWeeks)),
      escapeCsvField(formatDecimal(displayHc)),
      escapeCsvField(formatPercent(displayAllocation)),
    ].join(',');
  });

  csvRows.push(
    [
      'Total',
      escapeCsvField(formatDecimal(totalDevWeeks)),
      escapeCsvField(formatDecimal(totalHc)),
      '100%',
    ].join(',')
  );

  return [header.join(','), ...csvRows].join('\r\n');
};

const AuditWorkstreamRow = ({node, totalHc}: {node: AuditWorkstreamNode; totalHc: number}) => {
  const isGroup = node.children.length > 0;
  const displayDevWeeks = isGroup ? getAuditNodeTotalDevWeeks(node) : node.devWeeks;
  const displayHc = isGroup ? getAuditNodeTotalEstHc(node) : node.totalEstHc;
  const displayAllocation = totalHc > 0 ? displayHc / totalHc : 0;

  return (
    <Table.Row className={cx({[styles.auditNarrativeRow]: isGroup})}>
      <Table.Cell>
        <div className={styles.auditWorkstreamCell}>
          <RoadmapInitiativeLink title={node.title} jiraKey={node.key} />
          {node.initiatives.length > 0 ? (
            <Badge size="xsmall">{node.initiatives.length}</Badge>
          ) : null}
        </div>
      </Table.Cell>
      <Table.Cell>
        <Text size="small" className={styles.auditMetricText}>
          {formatDecimal(displayDevWeeks)}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small" className={styles.auditMetricText}>
          {formatDecimal(displayHc)}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small" className={styles.auditMetricText}>
          {formatPercent(displayAllocation)}
        </Text>
      </Table.Cell>
    </Table.Row>
  );
};

const AuditInvestmentRow = ({
  node,
  activeCategories,
}: {
  node: AuditInvestmentNode;
  activeCategories: string[];
}) => {
  return (
    <Table.Row>
      <Table.Cell>
        <div className={styles.auditWorkstreamCell}>
          <RoadmapInitiativeLink title={node.title} jiraKey={node.key} />
        </div>
      </Table.Cell>
      {activeCategories.map((category) => {
        const value = node.categories[category] ?? 0;
        return (
          <Table.Cell key={`${node.key}-${category}`}>
            <AuditInvestmentMetric
              value={value}
              total={node.totalDevWeeks}
              label={INVESTMENT_CATEGORY_SHORT_LABELS[category] ?? category}
            />
          </Table.Cell>
        );
      })}
      <Table.Cell>
        <AuditInvestmentTotalMetric total={node.totalDevWeeks} />
      </Table.Cell>
    </Table.Row>
  );
};

const DashboardLaunchRow = ({row, showOverdue}: {row: LaunchTableRow; showOverdue?: boolean}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdueItem = Boolean(showOverdue) && isOverdue(row.item, today);

  return (
    <Table.Row>
      <Table.Cell>
        <RoadmapInitiativeLink title={row.item.title} jiraKey={row.item.key} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapWorkstreamName item={row.item} lines={2} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapDriCell item={row.item} />
      </Table.Cell>
      <Table.Cell>
        <RoadmapJiraStatus
          status={isOverdueItem && !row.item.status ? 'Delayed' : row.item.status}
        />
      </Table.Cell>
      <Table.Cell>
        <RoadmapMetaDate date={row.item.endDate} isOverdueDate={isOverdueItem} />
      </Table.Cell>
      <Table.Cell>
        <DashboardDocumentLinks item={row.item} />
      </Table.Cell>
    </Table.Row>
  );
};

// `embedded` drops the scope sidebar and the document title and fills its
// container instead of the viewport (used by /workstreams).
export const Roadmap = ({embedded = false}: {embedded?: boolean}) => {
  const pageClassName = cx(styles.page, {[styles.pageEmbedded]: embedded});
  const contentRef = useRef<HTMLDivElement>(null);
  const previousActiveTabRef = useRef<RoadmapTab | null>(null);
  const [searchParams, setSearchParams] = useRoadmapSearchParams();
  const [roadmapGrouping, setRoadmapGrouping] = useState<RoadmapGrouping>('depth:1');
  const [sortOrder, setSortOrder] = useState<RoadmapSortOrder>('end_date');
  const [roadmapSortDirection, setRoadmapSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [selectedDashboardBucket, setSelectedDashboardBucket] =
    useState<DashboardProgressBucket | null>(null);
  const [hasOverviewShortcutStatusFilter, setHasOverviewShortcutStatusFilter] = useState(false);
  const [scopeSidebarCollapsed, setScopeSidebarCollapsed] = useState(false);
  const {employee: loggedInEmployee} = useAtomValue(loggedInEmployeeAtom);
  const {isProductRoadmapEnabled} = useAtomValue(growthbookCacheAtom);
  const searchState = useMemo(() => getRoadmapSearchState(searchParams), [searchParams]);
  const {
    activeTab: requestedActiveTab,
    audience: requestedAudience,
    hasExplicitAudienceSelection,
    requestedTimeframe,
    requestedScope,
    requestedPerson,
    requestedTeamId,
    requestedQuarter,
    viewMode,
    auditFilters,
  } = searchState;
  const {employee: selectedPerson, isLoading: selectedPersonLoading} = useEmployee({
    ldap: requestedAudience === 'person' ? (requestedPerson ?? undefined) : undefined,
    throwOnError: false,
  });
  const facetsQuery = useQuery({
    queryKey: ['roadmap', 'facets'],
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: fetchRoadmapFacets,
  });

  const selectedGroupTeam = useMemo(
    () =>
      requestedAudience === 'group' && requestedTeamId
        ? ((facetsQuery.data?.group_hierarchy ?? []).find(
            (team) => team.team_id.toLocaleLowerCase() === requestedTeamId.toLocaleLowerCase()
          ) ?? null)
        : null,
    [facetsQuery.data?.group_hierarchy, requestedAudience, requestedTeamId]
  );
  const selectedGroupTeamId = selectedGroupTeam?.team_id ?? null;
  const selectedGroupIds = selectedGroupTeam?.group_ids ?? [];

  const effectiveRequestedTimeframe = useMemo(
    () =>
      facetsQuery.data
        ? getRequestedOrCurrentRoadmapTimeframeKey({
            timeframes: facetsQuery.data.timeframes ?? [],
            requestedTimeframe,
            requestedQuarter,
            currentQuarter: getCurrentQuarterLabel(),
          })
        : null,
    [facetsQuery.data, requestedQuarter, requestedTimeframe]
  );

  const baseNavigation = useMemo(
    () =>
      facetsQuery.data
        ? resolveRoadmapNavigation(
            facetsQuery.data,
            null,
            effectiveRequestedTimeframe,
            requestedScope
          )
        : null,
    [effectiveRequestedTimeframe, facetsQuery.data, requestedScope]
  );

  const navigationQuery = useQuery({
    queryKey: ['roadmap', 'navigation', baseNavigation?.timeframe?.key ?? null],
    enabled: Boolean(facetsQuery.data && baseNavigation),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: () => fetchRoadmapNavigation(baseNavigation?.timeframe?.key),
  });

  const navigation = useMemo(
    () =>
      facetsQuery.data
        ? resolveRoadmapNavigation(
            facetsQuery.data,
            navigationQuery.data ?? null,
            effectiveRequestedTimeframe,
            requestedScope
          )
        : null,
    [effectiveRequestedTimeframe, facetsQuery.data, navigationQuery.data, requestedScope]
  );
  const selectedTimeframeKey = navigation?.timeframe?.key ?? null;
  const selectedScopeKey = requestedAudience === 'scope' ? (navigation?.scope?.key ?? null) : null;
  const timeframeQuarters = useMemo(
    () => getQuartersForRoadmapTimeframes(facetsQuery.data?.timeframes ?? []),
    [facetsQuery.data?.timeframes]
  );
  const roadmapRequestQuarter = useMemo(
    () =>
      getRoadmapRequestQuarter({
        timeframe: navigation?.timeframe ?? null,
        requestedQuarter,
        currentQuarter: getCurrentQuarterLabel(),
      }),
    [navigation?.timeframe, requestedQuarter]
  );
  const shouldLoadRoadmapDataset =
    requestedAudience === 'my' ||
    requestedAudience === 'person' ||
    requestedActiveTab === 'all' ||
    requestedActiveTab === 'roadmap' ||
    requestedActiveTab === 'allocations';
  const personalizedDatasetScope = useMemo(
    () => getPersonalizedRoadmapDatasetScope(requestedAudience, selectedPerson?.user_id ?? null),
    [requestedAudience, selectedPerson?.user_id]
  );
  const personalizedDatasetAssignee = personalizedDatasetScope?.assignee ?? null;
  const personalizedDatasetEmployeeId = personalizedDatasetScope?.assigneeScopeEmployeeId ?? null;
  const selectedPersonLookupState = getRoadmapPersonLookupState(
    requestedAudience,
    selectedPersonLoading,
    selectedPerson?.user_id ?? null
  );

  const personalizedRoadmapQuery = useQuery({
    queryKey: [
      'roadmap',
      'items',
      'personalized',
      personalizedDatasetAssignee,
      personalizedDatasetEmployeeId,
      selectedTimeframeKey,
      roadmapRequestQuarter,
    ],
    enabled:
      shouldLoadRoadmapDataset &&
      Boolean(selectedTimeframeKey) &&
      personalizedDatasetAssignee !== null,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: async () => {
      if (!personalizedDatasetAssignee) {
        throw new Error('Personalized Roadmap scope is unavailable');
      }
      const response = await fetchRoadmapData({
        timeframe: selectedTimeframeKey,
        scopeKey: null,
        quarter: roadmapRequestQuarter,
        assignee: personalizedDatasetAssignee,
        assigneeScopeEmployeeId: personalizedDatasetEmployeeId,
      });
      return buildRoadmapDatasetFromItems(response.items);
    },
  });

  const myRoadmapItemCount =
    requestedAudience === 'my' && personalizedRoadmapQuery.data
      ? personalizedRoadmapQuery.data.initiatives.length
      : null;
  const isDefaultAudiencePending =
    requestedAudience === 'my' && !hasExplicitAudienceSelection && myRoadmapItemCount == null;
  const audience = resolveRoadmapAudience({
    requestedAudience,
    hasExplicitSelection: hasExplicitAudienceSelection,
    myRoadmapItemCount,
  });
  const loadUnpersonalizedRoadmapDataset = shouldLoadUnpersonalizedRoadmapDataset({
    requestedAudience,
    hasExplicitAudienceSelection,
    myRoadmapItemCount,
  });
  const roadmapQuery = useQuery({
    queryKey: [
      'roadmap',
      'items',
      'unpersonalized',
      selectedTimeframeKey,
      selectedScopeKey,
      selectedGroupTeamId,
      selectedGroupIds,
      roadmapRequestQuarter,
    ],
    enabled:
      shouldLoadRoadmapDataset && loadUnpersonalizedRoadmapDataset && Boolean(selectedTimeframeKey),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await fetchRoadmapData({
        timeframe: selectedTimeframeKey,
        scopeKey: selectedScopeKey,
        groupIds: selectedGroupIds,
        quarter: roadmapRequestQuarter,
      });
      return buildRoadmapDatasetFromItems(response.items);
    },
  });
  const isPersonalizedRoadmap = audience === 'my' || audience === 'person';
  const roadmapContentAssignee = isPersonalizedRoadmap ? personalizedDatasetAssignee : 'all';
  const roadmapContentEmployeeId = isPersonalizedRoadmap ? personalizedDatasetEmployeeId : null;
  const roadmapExpansionContextKey = selectedTimeframeKey
    ? JSON.stringify([
        selectedTimeframeKey,
        audience,
        selectedScopeKey,
        selectedGroupTeamId,
        roadmapContentEmployeeId,
      ])
    : null;
  const {collapsedNodes, toggleNode} = useRoadmapNodeExpansion(roadmapExpansionContextKey);

  const isRoadmapContentAudienceResolved = roadmapContentAssignee !== null;
  const activeTab =
    !isPersonalizedRoadmap && requestedActiveTab === 'all' ? 'launches' : requestedActiveTab;
  const shouldLoadPersonalizedDashboard =
    activeTab === 'all' && isPersonalizedRoadmap && !isDefaultAudiencePending;
  const shouldLoadLaunches = activeTab === 'launches' || shouldLoadPersonalizedDashboard;
  const shouldLoadRollup = activeTab === 'rollup' || shouldLoadPersonalizedDashboard;
  const shouldLoadAuditFindings = activeTab === 'audit' || shouldLoadPersonalizedDashboard;
  const shouldLoadAllocations = !isPersonalizedRoadmap && activeTab === 'allocations';
  const effectiveRoadmapQuery = isPersonalizedRoadmap ? personalizedRoadmapQuery : roadmapQuery;
  const dataset = effectiveRoadmapQuery.data;

  const availableQuarters = useMemo(() => {
    return timeframeQuarters.length > 0 ? timeframeQuarters : (dataset?.quarters ?? []);
  }, [dataset?.quarters, timeframeQuarters]);

  const selectedQuarter = useMemo(() => {
    if (requestedQuarter && availableQuarters.includes(requestedQuarter)) {
      return requestedQuarter;
    }
    return getDefaultQuarterForRoadmapTimeframe(
      navigation?.timeframe ?? null,
      availableQuarters,
      getCurrentQuarterLabel()
    );
  }, [availableQuarters, navigation?.timeframe, requestedQuarter]);

  const selectedScopeTitle =
    audience === 'my'
      ? 'My roadmap'
      : audience === 'person'
        ? `Roadmap • ${selectedPerson?.name ?? requestedPerson ?? 'Person'}`
        : audience === 'group'
          ? `Roadmap • ${selectedGroupTeam?.team_name ?? 'Group'}`
          : audience === 'company'
            ? 'Roadmap • Company'
            : navigation?.scope?.label
              ? `Roadmap • ${navigation.scope.label}`
              : 'Roadmap';
  useDocumentTitle(embedded ? null : selectedScopeTitle);

  const statusOptions = useMemo(() => {
    if (!dataset) return [];
    return Array.from(
      new Set(
        dataset.initiatives
          .map((initiative) => normalizeRoadmapStatus(initiative.status))
          .filter((status): status is string => Boolean(status))
      )
    ).sort((left, right) => {
      const leftIndex = ROADMAP_STATUS_ORDER.indexOf(left as (typeof ROADMAP_STATUS_ORDER)[number]);
      const rightIndex = ROADMAP_STATUS_ORDER.indexOf(
        right as (typeof ROADMAP_STATUS_ORDER)[number]
      );
      const safeLeftIndex = leftIndex === -1 ? ROADMAP_STATUS_ORDER.length : leftIndex;
      const safeRightIndex = rightIndex === -1 ? ROADMAP_STATUS_ORDER.length : rightIndex;
      return safeLeftIndex - safeRightIndex || left.localeCompare(right);
    });
  }, [dataset]);

  useEffect(() => {
    setSelectedStatuses((current) => {
      const next = new Set([...current].filter((status) => statusOptions.includes(status)));
      if (next.size === current.size) {
        return current;
      }
      return next;
    });
  }, [statusOptions]);

  const baseRoadmapInitiatives = useMemo(() => {
    if (!dataset) return [];
    return dataset.initiatives.filter((initiative) =>
      initiativeMatchesRoadmapQuarter(initiative, selectedQuarter)
    );
  }, [dataset, selectedQuarter]);
  const effectiveSortOrder: RoadmapSortOrder = viewMode === 'gantt' ? 'end_date' : sortOrder;
  const effectiveSortDirection: 'asc' | 'desc' =
    viewMode === 'gantt' ? 'asc' : roadmapSortDirection;

  const roadmapInitiatives = useMemo(
    () =>
      [...baseRoadmapInitiatives]
        .filter((initiative) =>
          selectedStatuses.size === 0
            ? true
            : selectedStatuses.has(normalizeRoadmapStatus(initiative.status) ?? '')
        )
        .sort((left, right) => {
          const comparison = compareItems(left, right, effectiveSortOrder, selectedQuarter);
          return effectiveSortDirection === 'asc' ? comparison : comparison * -1;
        }),
    [
      baseRoadmapInitiatives,
      effectiveSortDirection,
      effectiveSortOrder,
      selectedQuarter,
      selectedStatuses,
    ]
  );

  const scopedHierarchyItems = useMemo(
    () => dataset?.hierarchyItems.filter((item) => item.key !== selectedScopeKey) ?? [],
    [dataset, selectedScopeKey]
  );
  const selectedScopeHierarchyItem = useMemo(
    () => dataset?.hierarchyItems.find((item) => item.key === selectedScopeKey) ?? null,
    [dataset, selectedScopeKey]
  );
  const roadmapGroupingOptions = useMemo(
    () => buildRoadmapGroupingOptions(scopedHierarchyItems),
    [scopedHierarchyItems]
  );
  const effectiveRoadmapGrouping = resolveRoadmapGrouping(roadmapGrouping, roadmapGroupingOptions);

  useEffect(() => {
    const rawTab = searchParams.get('tab');
    const canonicalTab =
      rawTab === 'dashboard'
        ? isPersonalizedRoadmap
          ? 'all'
          : 'launches'
        : rawTab === 'overview' || (rawTab === 'all' && !isPersonalizedRoadmap)
          ? 'launches'
          : null;
    if (!canonicalTab || rawTab === canonicalTab) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', canonicalTab);
        return next;
      },
      {replace: true}
    );
  }, [isPersonalizedRoadmap, searchParams, setSearchParams]);

  useEffect(() => {
    if (isDefaultAudiencePending || !facetsQuery.data || !navigation || navigationQuery.isLoading) {
      return;
    }
    const canonicalTimeframe = navigation.timeframe?.key ?? null;
    const canonicalScope = audience === 'scope' ? (navigation.scope?.key ?? null) : null;
    const canonicalPerson = audience === 'person' ? requestedPerson : null;
    const canonicalTeamId = audience === 'group' ? selectedGroupTeamId : null;
    const canonicalAudience =
      audience === 'company'
        ? 'company'
        : audience === 'my' && hasExplicitAudienceSelection
          ? 'my'
          : null;
    if (
      searchParams.get('timeframe') === canonicalTimeframe &&
      searchParams.get('scope') === canonicalScope &&
      searchParams.get('person') === canonicalPerson &&
      searchParams.get('team') === canonicalTeamId &&
      searchParams.get('audience') === canonicalAudience &&
      !searchParams.has('group') &&
      !searchParams.has('narrative')
    ) {
      return;
    }
    setSearchParams(
      (current) =>
        buildRoadmapSearchParams(current, {
          timeframe: canonicalTimeframe,
          scope: canonicalScope,
          person: canonicalPerson,
          team: canonicalTeamId,
          audience: canonicalAudience,
          group: null,
          narrative: null,
        }),
      {replace: true}
    );
  }, [
    audience,
    facetsQuery.data,
    hasExplicitAudienceSelection,
    navigationQuery.isLoading,
    isDefaultAudiencePending,
    navigation,
    requestedPerson,
    searchParams,
    selectedGroupTeamId,
    setSearchParams,
  ]);

  useEffect(() => {
    contentRef.current?.scrollTo({top: 0, left: 0, behavior: 'auto'});
  }, [activeTab]);

  useEffect(() => {
    const previousActiveTab = previousActiveTabRef.current;
    if (
      previousActiveTab === 'roadmap' &&
      activeTab !== 'roadmap' &&
      hasOverviewShortcutStatusFilter
    ) {
      setSelectedStatuses(new Set());
      setSelectedDashboardBucket(null);
      setHasOverviewShortcutStatusFilter(false);
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab, hasOverviewShortcutStatusFilter]);

  const roadmapTree = useMemo<RoadmapHierarchyNode[]>(() => {
    const hierarchyMap = new Map(scopedHierarchyItems.map((item) => [item.key, item]));
    const childrenByParent = groupHierarchyItemsByParent(scopedHierarchyItems);
    const depthByKey = getRoadmapHierarchyDepths(scopedHierarchyItems);
    const maxVisibleDepth = getRoadmapGroupingMaxDepth(effectiveRoadmapGrouping);

    const sortVisibleInitiatives = (items: RoadmapItem[]) =>
      [...items].sort((left, right) => compareItems(left, right, sortOrder, selectedQuarter));

    const initiativesByParent = new Map<string, RoadmapItem[]>();

    const findAppropriateParent = (initiative: RoadmapItem) => {
      const ancestryItems = initiative.ancestorKeys
        .map((key) => hierarchyMap.get(key))
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
      const visibleAncestor = ancestryItems
        .filter((item) => (depthByKey.get(item.key) ?? 0) <= maxVisibleDepth)
        .at(-1);
      const visibleParent = initiative.parentKey
        ? hierarchyMap.get(initiative.parentKey)
        : undefined;
      return (
        visibleAncestor?.key ??
        (visibleParent && (depthByKey.get(visibleParent.key) ?? 0) <= maxVisibleDepth
          ? visibleParent.key
          : 'root')
      );
    };

    roadmapInitiatives.forEach((initiative) => {
      const parentKey = findAppropriateParent(initiative);
      const existing = initiativesByParent.get(parentKey) ?? [];
      existing.push(initiative);
      initiativesByParent.set(parentKey, existing);
    });

    const collectAllInitiatives = (node: RoadmapHierarchyNode): RoadmapItem[] => [
      ...node.initiatives,
      ...node.children.flatMap((child) => collectAllInitiatives(child)),
    ];

    const buildNode = (
      item: (typeof scopedHierarchyItems)[number],
      currentDepth: number
    ): RoadmapHierarchyNode => {
      const childItems = (childrenByParent.get(item.key) ?? []).sort((left, right) =>
        sortRoadmapHierarchyItems(left, right)
      );

      const showChildren = currentDepth < maxVisibleDepth;
      const children: RoadmapHierarchyNode[] = showChildren
        ? childItems.map((child) => buildNode(child, currentDepth + 1))
        : [];

      let nodeInitiatives = initiativesByParent.get(item.key) ?? [];

      if (!showChildren && childItems.length > 0) {
        const collapsedChildren = childItems.map((child) => buildNode(child, currentDepth + 1));
        nodeInitiatives = sortVisibleInitiatives([
          ...nodeInitiatives,
          ...collapsedChildren.flatMap((child) => collectAllInitiatives(child)),
        ]);
      }

      return {
        key: item.key,
        title: item.title,
        issueType: item.issueType,
        workstreamId: item.workstreamId,
        workstreamArchived: item.workstreamArchived,
        children,
        initiatives: sortVisibleInitiatives(nodeInitiatives),
      };
    };

    const rootItems = scopedHierarchyItems
      .filter((item) => {
        if (!item.parentKey) return true;
        return !hierarchyMap.has(item.parentKey);
      })
      .sort((left, right) => sortRoadmapHierarchyItems(left, right));

    const tree = pruneArchivedEmptyRoadmapHierarchy(rootItems.map((item) => buildNode(item, 0)));
    const treeWithCurrentScope = prependCurrentScopeInitiatives(
      tree,
      selectedScopeHierarchyItem,
      sortVisibleInitiatives(initiativesByParent.get('root') ?? [])
    );
    return pruneEmptyRoadmapHierarchy(treeWithCurrentScope);
  }, [
    effectiveRoadmapGrouping,
    roadmapInitiatives,
    scopedHierarchyItems,
    selectedQuarter,
    selectedScopeHierarchyItem,
    sortOrder,
  ]);

  const launchesQuery = useQuery({
    queryKey: getRoadmapContentQueryKey('launches', {
      assignee: roadmapContentAssignee,
      assigneeScopeEmployeeId: roadmapContentEmployeeId,
      timeframe: selectedTimeframeKey,
      scopeKey: selectedScopeKey,
      groupTeamId: selectedGroupTeamId,
      groupIds: selectedGroupIds,
      quarter: selectedQuarter,
    }),
    enabled:
      shouldLoadLaunches && Boolean(selectedTimeframeKey) && isRoadmapContentAudienceResolved,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: () => {
      if (!roadmapContentAssignee) {
        throw new Error('Roadmap content audience is unavailable');
      }
      return fetchRoadmapLaunches({
        timeframe: selectedTimeframeKey,
        scopeKey: selectedScopeKey,
        groupIds: selectedGroupIds,
        quarter: selectedQuarter,
        assignee: roadmapContentAssignee,
        assigneeScopeEmployeeId: roadmapContentEmployeeId,
      });
    },
  });

  const allocationsQuery = useQuery({
    queryKey: [
      'roadmap',
      'allocations',
      selectedTimeframeKey,
      selectedScopeKey,
      selectedGroupTeamId,
      selectedGroupIds,
      selectedQuarter,
    ],
    enabled: shouldLoadAllocations && Boolean(selectedTimeframeKey),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: () =>
      fetchRoadmapAllocations({
        timeframe: selectedTimeframeKey,
        scopeKey: selectedScopeKey,
        groupIds: selectedGroupIds,
        quarter: selectedQuarter,
      }),
  });

  const effectiveAuditFilters = useMemo<RoadmapAuditFilters>(() => {
    if (audience === 'my') {
      return {...auditFilters, assignee: 'me', assigneeKeys: []};
    }
    if (audience === 'person' && selectedPerson?.user_id) {
      return {
        ...auditFilters,
        assignee: 'all',
        assigneeKeys: [`employee:${selectedPerson.user_id}`],
      };
    }
    return auditFilters;
  }, [audience, auditFilters, selectedPerson?.user_id]);

  const auditFindingsQuery = useRoadmapAuditFindings({
    enabled: shouldLoadAuditFindings && (audience !== 'person' || Boolean(selectedPerson?.user_id)),
    selection: {
      timeframe: selectedTimeframeKey,
      scopeKey: selectedScopeKey,
      groupIds: selectedGroupIds,
      quarter: selectedQuarter,
      ...effectiveAuditFilters,
      assigneeScopeEmployeeId: audience === 'person' ? (selectedPerson?.user_id ?? null) : null,
    },
  });

  const rollupQuery = useQuery({
    queryKey: getRoadmapContentQueryKey('rollup', {
      assignee: roadmapContentAssignee,
      assigneeScopeEmployeeId: roadmapContentEmployeeId,
      timeframe: selectedTimeframeKey,
      scopeKey: selectedScopeKey,
      groupTeamId: selectedGroupTeamId,
      groupIds: selectedGroupIds,
      quarter: selectedQuarter,
    }),
    enabled: shouldLoadRollup && Boolean(selectedTimeframeKey) && isRoadmapContentAudienceResolved,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: () => {
      if (!roadmapContentAssignee) {
        throw new Error('Roadmap content audience is unavailable');
      }
      return fetchRoadmapRollup({
        timeframe: selectedTimeframeKey as string,
        scopeKey: selectedScopeKey,
        groupIds: selectedGroupIds,
        quarter: selectedQuarter,
        assignee: roadmapContentAssignee,
        assigneeScopeEmployeeId: roadmapContentEmployeeId,
      });
    },
  });

  const roadmapNodeUpdatesByWorkstreamId = useMemo(() => {
    if (!dataset) return EMPTY_ROADMAP_NODE_UPDATES;

    const updatesByWorkstreamId = new Map<number, RoadmapNodeUpdate>();
    for (const item of dataset.items) {
      if (item.workstreamId && item.latestWorkstreamUpdate) {
        updatesByWorkstreamId.set(item.workstreamId, item.latestWorkstreamUpdate);
      }
    }
    return updatesByWorkstreamId;
  }, [dataset]);

  const displayedRollup = rollupQuery.data;

  const quarterEstimateKey = useMemo(() => {
    const normalizedQuarter = normalizeQuarterLabel(selectedQuarter);
    return normalizedQuarter
      ? (`q${normalizedQuarter.split(' Q')[1]}` as 'q1' | 'q2' | 'q3' | 'q4')
      : 'q1';
  }, [selectedQuarter]);

  const personalizedAllocations = useMemo(
    () =>
      isPersonalizedRoadmap
        ? buildMyRoadmapAllocations(baseRoadmapInitiatives, quarterEstimateKey)
        : null,
    [baseRoadmapInitiatives, isPersonalizedRoadmap, quarterEstimateKey]
  );
  const groupedAllocationWorkstreams = useMemo(
    () =>
      aggregateAllocationsByHierarchy(
        personalizedAllocations?.workstreams ?? allocationsQuery.data?.workstreams,
        dataset
      ),
    [allocationsQuery.data?.workstreams, dataset, personalizedAllocations?.workstreams]
  );

  const auditWorkstreamAllocation = useMemo(() => {
    const workstreamTree: AuditWorkstreamNode[] = groupedAllocationWorkstreams
      .map((workstream) => ({
        key: workstream.jira_key,
        title: workstream.title,
        issueType: dataset?.itemsByKey.get(workstream.jira_key)?.issueType ?? 'Hierarchy Node',
        devWeeks: workstream.dev_estimates?.[quarterEstimateKey] ?? 0,
        totalEstHc: workstream.total_headcount ?? 0,
        children: [],
        initiatives: [],
      }))
      .sort((left, right) => left.title.localeCompare(right.title, undefined, {numeric: true}));

    return {
      workstreamTree,
      totalDevWeeks: workstreamTree.reduce((sum, node) => sum + node.devWeeks, 0),
      totalHc: workstreamTree.reduce((sum, node) => sum + node.totalEstHc, 0),
    };
  }, [dataset?.itemsByKey, groupedAllocationWorkstreams, quarterEstimateKey]);

  const investmentCategoryData = useMemo(() => {
    const tree: AuditInvestmentNode[] = groupedAllocationWorkstreams
      .map((workstream) => {
        const categories = workstream.investment_categories ?? {};
        const total = Object.values(categories).reduce((sum, value) => sum + value, 0);

        return {
          key: workstream.jira_key,
          title: workstream.title,
          categories,
          totalDevWeeks: total,
          children: [],
        };
      })
      .sort((left, right) => left.title.localeCompare(right.title, undefined, {numeric: true}));

    const allCategories = tree.reduce<Record<string, number>>(
      (accumulator, node) =>
        Object.entries(node.categories).reduce<Record<string, number>>(
          (nextAccumulator, [category, value]) => ({
            ...nextAccumulator,
            [category]: (nextAccumulator[category] ?? 0) + value,
          }),
          accumulator
        ),
      {}
    );

    const presentCategories = new Set<string>();
    tree.forEach((node) => {
      Object.keys(node.categories).forEach((category) => presentCategories.add(category));
    });

    const activeCategories: string[] = [...INVESTMENT_CATEGORY_ORDER];
    [...presentCategories]
      .filter(
        (category) =>
          !activeCategories.includes(category as (typeof INVESTMENT_CATEGORY_ORDER)[number])
      )
      .sort((left, right) => left.localeCompare(right))
      .forEach((category) => activeCategories.push(category));

    return {
      tree,
      grandTotal: tree.reduce((sum, node) => sum + node.totalDevWeeks, 0),
      allCategories,
      activeCategories,
    };
  }, [groupedAllocationWorkstreams]);
  const flatAuditWorkstreamRows = useMemo(
    () => flattenAuditWorkstreamNodes(auditWorkstreamAllocation.workstreamTree),
    [auditWorkstreamAllocation.workstreamTree]
  );
  const flatAuditInvestmentRows = useMemo(
    () => flattenAuditInvestmentNodes(investmentCategoryData.tree),
    [investmentCategoryData.tree]
  );

  const totalLaunchCount = useMemo(
    () =>
      dataset?.initiatives.filter(
        (initiative) =>
          initiative.deliveryQuarter === selectedQuarter && Boolean(initiative.endDate)
      ).length ?? 0,
    [dataset, selectedQuarter]
  );

  const launches = useMemo(
    () => ({
      total: totalLaunchCount,
      recent: launchesQuery.data?.recent ?? [],
      overdue: launchesQuery.data?.overdue ?? [],
      upcoming: launchesQuery.data?.upcoming ?? [],
      cancelled: launchesQuery.data?.cancelled ?? [],
    }),
    [launchesQuery.data, totalLaunchCount]
  );

  const dashboardProgress = useMemo(() => {
    const total = baseRoadmapInitiatives.length;
    const buckets = {
      total,
      completed: 0,
      onTrack: 0,
      atRiskDelayed: 0,
      notStarted: 0,
      cancelled: 0,
    };

    baseRoadmapInitiatives.forEach((initiative) => {
      const bucket = getRoadmapProgressBucket(initiative.status);

      if (!bucket) return;
      if (bucket === 'completed') buckets.completed += 1;
      if (bucket === 'onTrack') buckets.onTrack += 1;
      if (bucket === 'atRiskDelayed') buckets.atRiskDelayed += 1;
      if (bucket === 'notStarted') buckets.notStarted += 1;
      if (bucket === 'cancelled') buckets.cancelled += 1;
    });

    return buckets;
  }, [baseRoadmapInitiatives]);

  const dashboardDependencyCount = useMemo(
    () => getRollupDependencyCount(displayedRollup),
    [displayedRollup]
  );

  const peopleDashboardData = useMemo<PeopleRoadmapDashboardData>(() => {
    const roadmapRows = [...baseRoadmapInitiatives]
      .sort((left, right) => compareItems(left, right, 'end_date', selectedQuarter))
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map((initiative) => ({
        id: initiative.key,
        cells: [
          initiative.title,
          normalizeRoadmapStatus(initiative.status) ?? '—',
          formatDateWithYear(initiative.endDate),
        ],
      }));

    const seenLaunchKeys = new Set<string>();
    const launchRows = [
      ...launches.overdue.map((item) => ({item, timing: 'Overdue'})),
      ...launches.upcoming.map((item) => ({item, timing: 'Upcoming'})),
      ...launches.recent.map((item) => ({item, timing: 'Recent'})),
      ...launches.cancelled.map((item) => ({item, timing: 'Cancelled'})),
    ]
      .filter(({item}) => {
        if (seenLaunchKeys.has(item.key)) return false;
        seenLaunchKeys.add(item.key);
        return true;
      })
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map(({item, timing}) => ({
        id: item.key,
        cells: [item.title, timing, formatDateWithYear(item.endDate)],
      }));

    const dependencyRows = (displayedRollup?.rows ?? [])
      .filter(
        (row) =>
          row.node.semantic_role === RoadmapNodeRole.ROADMAP_INITIATIVE &&
          (row.metrics.structural.dependency_count ?? 0) > 0
      )
      .sort(
        (left, right) =>
          (right.metrics.structural.dependency_count ?? 0) -
            (left.metrics.structural.dependency_count ?? 0) ||
          left.node.title.localeCompare(right.node.title)
      )
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map((row) => ({
        id: row.node.key,
        cells: [
          row.node.title,
          formatDecimal(row.metrics.structural.dependency_count ?? 0),
          row.node.planning_quarter ?? '—',
        ],
      }));

    const rollupCandidates = (displayedRollup?.rows ?? []).filter(
      (row) =>
        row.node.semantic_role !== RoadmapNodeRole.ROADMAP_INITIATIVE &&
        (row.metrics.structural.initiative_count ?? 0) > 0
    );
    const deepestRollupDepth = rollupCandidates.reduce(
      (deepest, row) => Math.max(deepest, row.node.depth),
      Number.MIN_SAFE_INTEGER
    );
    const rollupRows = rollupCandidates
      .filter((row) => row.node.depth === deepestRollupDepth)
      .sort(
        (left, right) =>
          (right.metrics.estimates.total_dev_weeks ?? 0) -
            (left.metrics.estimates.total_dev_weeks ?? 0) ||
          left.node.title.localeCompare(right.node.title)
      )
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map((row) => ({
        id: row.node.key,
        cells: [
          row.node.title,
          formatDecimal(row.metrics.structural.initiative_count ?? 0),
          formatDecimal(row.metrics.estimates.total_dev_weeks ?? 0),
        ],
      }));

    const allocationRows = [...groupedAllocationWorkstreams]
      .sort(
        (left, right) =>
          (right.dev_estimates?.[quarterEstimateKey] ?? 0) -
            (left.dev_estimates?.[quarterEstimateKey] ?? 0) || left.title.localeCompare(right.title)
      )
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map((workstream) => ({
        id: workstream.jira_key,
        cells: [
          workstream.title,
          formatDecimal(workstream.dev_estimates?.[quarterEstimateKey] ?? 0),
          formatDecimal(workstream.total_headcount ?? 0),
        ],
      }));

    const auditRows = (auditFindingsQuery.data?.items ?? [])
      .slice(0, PEOPLE_DASHBOARD_ROW_LIMIT)
      .map((finding) => ({
        id: finding.initiative.key,
        cells: [finding.initiative.title, ''],
        violations: finding.violations,
      }));

    const rollupDevWeeks = displayedRollup?.diagnostics.selected_quarter_total_dev_weeks;
    const rollupHeadcount = displayedRollup?.diagnostics.selected_quarter_total_headcount;
    const auditCount = auditFindingsQuery.data?.summary.flagged_initiative_count;

    return {
      roadmap: {
        summary: `${dashboardProgress.total} initiatives`,
        columns: ['Initiative', 'Status', 'End date'],
        rows: roadmapRows,
        statusColumnIndex: 1,
        emptyMessage: 'No initiatives in this quarter.',
      },
      launches: {
        summary: `${launches.upcoming.length} upcoming · ${launches.overdue.length} overdue · ${launches.cancelled.length} cancelled`,
        columns: ['Initiative', 'Timing', 'End date'],
        rows: launchRows,
        emptyMessage: 'No recent, upcoming, overdue, or cancelled launches.',
      },
      dependencies: {
        summary:
          dashboardDependencyCount == null
            ? 'Loading linked dependencies…'
            : `${dashboardDependencyCount} linked`,
        columns: ['Initiative', 'Linked', 'Quarter'],
        rows: dependencyRows,
        emptyMessage:
          dashboardDependencyCount == null
            ? 'Loading dependency details…'
            : 'No linked dependencies.',
      },
      rollup: {
        summary:
          rollupDevWeeks == null || rollupHeadcount == null
            ? 'Loading totals…'
            : `${formatDecimal(rollupDevWeeks)} dev weeks · ${formatDecimal(rollupHeadcount)} HC`,
        columns: ['Area', 'Initiatives', 'Dev weeks'],
        rows: rollupRows,
        emptyMessage: rollupDevWeeks == null ? 'Loading rollup details…' : 'No rollup details.',
      },
      allocations: {
        summary: `${groupedAllocationWorkstreams.length} workstreams`,
        columns: ['Workstream', 'Dev weeks', 'Est. HC'],
        rows: allocationRows,
        emptyMessage: 'No allocation details.',
      },
      audit: {
        summary: auditCount == null ? 'Loading findings…' : `${auditCount} flagged`,
        columns: ['Initiative', 'Flags'],
        rows: auditRows,
        flagsColumnIndex: 1,
        emptyMessage: auditCount == null ? 'Loading audit findings…' : 'No audit findings.',
      },
    };
  }, [
    auditFindingsQuery.data,
    baseRoadmapInitiatives,
    dashboardDependencyCount,
    dashboardProgress.total,
    groupedAllocationWorkstreams,
    launches.cancelled,
    launches.overdue,
    launches.recent,
    launches.upcoming,
    displayedRollup,
    quarterEstimateKey,
    selectedQuarter,
  ]);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams(buildRoadmapSearchParams(searchParams, updates));
  };

  const handleAuditFiltersChange = (nextFilters: RoadmapAuditFilters) => {
    setSearchParams(
      buildRoadmapAuditSearchParams(searchParams, {
        ...nextFilters,
        ...(audience === 'my' ? {assignee: 'me' as const, assigneeKeys: []} : {}),
        ...(audience === 'person' && selectedPerson?.user_id
          ? {
              assignee: 'all' as const,
              assigneeKeys: [`employee:${selectedPerson.user_id}`],
            }
          : {}),
      })
    );
  };

  const topTabs = useMemo<GroupTab[]>(() => {
    const counts: Record<RoadmapTab, number> = {
      all: baseRoadmapInitiatives.length,
      launches: launches.total,
      roadmap: baseRoadmapInitiatives.length,
      dependencies: dashboardDependencyCount ?? 0,
      rollup: displayedRollup?.diagnostics.row_count ?? 0,
      allocations:
        auditWorkstreamAllocation.workstreamTree.length + investmentCategoryData.tree.length,
      audit: auditFindingsQuery.data?.summary.flagged_initiative_count ?? 0,
    };

    return getRoadmapTabDefinitions(isPersonalizedRoadmap).map(({id, label}) => ({
      id,
      label,
      count: counts[id],
      disabled: false,
    }));
  }, [
    auditWorkstreamAllocation.workstreamTree.length,
    baseRoadmapInitiatives.length,
    investmentCategoryData.tree.length,
    isPersonalizedRoadmap,
    launches.total,
    dashboardDependencyCount,
    displayedRollup?.diagnostics.row_count,
    auditFindingsQuery.data?.summary.flagged_initiative_count,
  ]);

  const currentQuarterWindow = useMemo(
    () => getQuarterDateRange(selectedQuarter),
    [selectedQuarter]
  );
  const ganttTimeline = useMemo(
    () => generateGanttTimelineLabels(currentQuarterWindow.start, currentQuarterWindow.end),
    [currentQuarterWindow]
  );
  const ganttToday = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  const handleDashboardBucketClick = (bucket: DashboardProgressBucket) => {
    updateParams({tab: 'roadmap'});
    const nextBucket = bucket === 'total' || selectedDashboardBucket === bucket ? null : bucket;
    const nextStatuses = nextBucket
      ? statusOptions.filter((status) => getRoadmapProgressBucket(status) === nextBucket)
      : null;
    setSelectedDashboardBucket(nextBucket);
    setSelectedStatuses(nextStatuses ? new Set(nextStatuses) : new Set());
    setHasOverviewShortcutStatusFilter(nextBucket !== null);
  };

  const resetSidebarNavigationState = () => {
    setRoadmapGrouping('depth:1');
    setSortOrder('end_date');
    setRoadmapSortDirection('asc');
    setSelectedStatuses(new Set());
    setSelectedDashboardBucket(null);
    setHasOverviewShortcutStatusFilter(false);
  };

  const handleSelectQuarter = (quarter: string) => {
    if (!facetsQuery.data) return;
    const nextTimeframe = findRoadmapTimeframeForQuarter(
      facetsQuery.data.timeframes ?? [],
      quarter
    );
    if (nextTimeframe && nextTimeframe.key !== selectedTimeframeKey) {
      resetSidebarNavigationState();
      const nextNavigation = resolveRoadmapNavigation(
        facetsQuery.data,
        null,
        nextTimeframe.key,
        null
      );
      updateParams({
        quarter,
        timeframe: nextNavigation.timeframe?.key ?? null,
        scope: audience === 'scope' ? (nextNavigation.scope?.key ?? null) : null,
        group: null,
        narrative: null,
        view: 'table',
        auditNarrative: null,
      });
      return;
    }
    updateParams({quarter, auditNarrative: null});
  };

  const handleSelectScope = (scopeKey: string) => {
    resetSidebarNavigationState();
    updateParams({
      audience: null,
      person: null,
      team: null,
      scope: scopeKey,
      group: null,
      narrative: null,
      view: 'table',
      auditNarrative: null,
    });
  };

  const handleSelectMyRoadmap = () => {
    resetSidebarNavigationState();
    updateParams({
      audience: 'my',
      person: null,
      team: null,
      scope: null,
      group: null,
      narrative: null,
      view: 'table',
      auditNarrative: null,
    });
  };

  const handleSelectCompany = () => {
    resetSidebarNavigationState();
    updateParams({
      audience: 'company',
      person: null,
      team: null,
      scope: null,
      group: null,
      narrative: null,
      view: 'table',
      auditNarrative: null,
    });
  };

  const handleSelectPerson = (person: PersonOption) => {
    resetSidebarNavigationState();
    updateParams({
      audience: null,
      person: person.ldap,
      team: null,
      scope: null,
      group: null,
      narrative: null,
      view: 'table',
      auditNarrative: null,
    });
  };

  const handleSelectGroup = (team: RoadmapGroupHierarchyTeam) => {
    resetSidebarNavigationState();
    updateParams({
      audience: null,
      person: null,
      team: team.team_id,
      scope: null,
      group: null,
      narrative: null,
      view: 'table',
      auditNarrative: null,
    });
  };

  const toggleRoadmapStatus = (status: string) => {
    setHasOverviewShortcutStatusFilter(false);
    setSelectedDashboardBucket(null);
    setSelectedStatuses((current) => {
      const next = new Set(current);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const clearRoadmapStatusFilters = () => {
    setHasOverviewShortcutStatusFilter(false);
    setSelectedDashboardBucket(null);
    setSelectedStatuses(new Set());
  };

  const roadmapColumns = useMemo<Column<RoadmapItem>[]>(
    () => [
      {
        type: 'initiative',
        minWidth: INITIATIVE_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'owner',
        minWidth: DRI_COLUMN_MIN_WIDTH,
        width: DRI_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'status',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        width: STATUS_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'start_date',
        minWidth: START_DATE_COLUMN_MIN_WIDTH,
        width: START_DATE_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'end_date',
        minWidth: END_DATE_COLUMN_MIN_WIDTH,
        width: END_DATE_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'links',
        minWidth: RESOURCES_COLUMN_MIN_WIDTH,
        width: RESOURCES_COLUMN_WIDTH,
        cantDisable: true,
      },
    ],
    []
  );

  const allocationColumns = useMemo<Column<AuditWorkstreamNode>[]>(
    () => [
      {
        type: 'workstream',
        minWidth: WORKSTREAM_COLUMN_MIN_WIDTH,
        spanner: true,
        cantDisable: true,
      },
      {
        type: 'dev_weeks',
        fixed: true,
        minWidth: ALLOCATION_CATEGORY_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'total_est_hc',
        fixed: true,
        minWidth: ALLOCATION_CATEGORY_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'allocation_percent',
        fixed: true,
        minWidth: ALLOCATION_CATEGORY_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
    ],
    []
  );

  const investmentColumns = useMemo<Column<AuditInvestmentNode>[]>(
    () => [
      {
        type: 'workstream',
        minWidth: WORKSTREAM_COLUMN_MIN_WIDTH,
        spanner: true,
        cantDisable: true,
      },
      ...investmentCategoryData.activeCategories.map((category) => {
        const label = INVESTMENT_CATEGORY_SHORT_LABELS[category] ?? category;
        return {
          type: `investment_${category}`,
          fixed: true,
          minWidth: getInvestmentCategoryColumnWidth(label),
          cantDisable: true,
        };
      }),
      {
        type: 'total',
        fixed: true,
        minWidth: getInvestmentCategoryColumnWidth('Total'),
        cantDisable: true,
      },
    ],
    [investmentCategoryData.activeCategories]
  );

  const launchColumns = useMemo<Column<LaunchTableRow>[]>(
    () => [
      {
        type: 'initiative',
        minWidth: INITIATIVE_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'workstream',
        minWidth: WORKSTREAM_COLUMN_MIN_WIDTH,
        cantDisable: true,
      },
      {type: 'owner', minWidth: DRI_COLUMN_MIN_WIDTH, width: DRI_COLUMN_WIDTH, cantDisable: true},
      {
        type: 'status',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        width: STATUS_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'date',
        minWidth: END_DATE_COLUMN_MIN_WIDTH,
        width: END_DATE_COLUMN_WIDTH,
        cantDisable: true,
      },
      {
        type: 'links',
        minWidth: RESOURCES_COLUMN_MIN_WIDTH,
        width: RESOURCES_COLUMN_WIDTH,
        cantDisable: true,
      },
    ],
    []
  );

  const handleExportHeadcount = () => {
    const csv = buildHeadcountCsv(
      flatAuditWorkstreamRows,
      auditWorkstreamAllocation.totalDevWeeks,
      auditWorkstreamAllocation.totalHc
    );
    downloadCsv(csv, 'roadmap-headcount-allocation.csv');
  };

  const roadmapLoadState = getRoadmapLoadState({
    facetsLoading: facetsQuery.isLoading,
    facetsError: facetsQuery.error,
    datasetRequired: shouldLoadRoadmapDataset,
    hasDataset: Boolean(dataset),
    datasetLoading: effectiveRoadmapQuery.isLoading || selectedPersonLookupState === 'loading',
    datasetError:
      selectedPersonLookupState === 'not_found'
        ? new Error("Couldn't find this person.")
        : effectiveRoadmapQuery.error,
  });
  const blockingShellLoading = roadmapLoadState.shellLoading;
  const activeContentLoading =
    roadmapLoadState.contentLoading ||
    (shouldLoadRoadmapDataset &&
      effectiveRoadmapQuery.isPlaceholderData &&
      effectiveRoadmapQuery.isFetching) ||
    (activeTab === 'launches' && !launchesQuery.data && launchesQuery.isLoading) ||
    (activeTab === 'rollup' && !rollupQuery.data && rollupQuery.isLoading) ||
    (activeTab === 'allocations' && !allocationsQuery.data && allocationsQuery.isLoading);

  const blockingShellError = roadmapLoadState.shellError;
  const activeContentError =
    roadmapLoadState.contentError ??
    (activeTab === 'launches' && !launchesQuery.data ? launchesQuery.error : null) ??
    (activeTab === 'rollup' && !rollupQuery.data ? rollupQuery.error : null) ??
    (activeTab === 'allocations' && !allocationsQuery.data ? allocationsQuery.error : null);
  const showActiveContentLoading =
    activeContentLoading ||
    (!facetsQuery.error && isRoadmapSnapshotUnavailableError(activeContentError));
  const canRenderActiveContent = !showActiveContentLoading && !activeContentError;

  const hasNoScopes = Boolean(facetsQuery.data && navigation && !navigation.scope);
  const showShellLoading =
    blockingShellLoading ||
    (!facetsQuery.error && isRoadmapSnapshotUnavailableError(blockingShellError));
  const pageFadeInRef = useSkeletonFadeIn(showShellLoading);
  useSkeletonFadeIn(showActiveContentLoading, contentRef);

  if (showShellLoading) {
    return <RoadmapLoadingSkeleton embedded={embedded} />;
  }

  if (hasNoScopes) {
    return (
      <div className={pageClassName}>
        <div className={styles.main}>
          <div className={styles.header}>
            <div className={styles.headerTopRow}>
              <Title size={24}>Roadmap</Title>
              {availableQuarters.length > 0 ? (
                <RoadmapQuarterControl
                  availableQuarters={availableQuarters}
                  selectedQuarter={selectedQuarter}
                  onSelect={handleSelectQuarter}
                />
              ) : null}
            </div>
            <Box paddingTop="16">
              <Text color="subtle">No roadmap scopes are available for this timeframe.</Text>
            </Box>
          </div>
        </div>
      </div>
    );
  }

  if (blockingShellError) {
    const errorMessage =
      blockingShellError instanceof Error
        ? blockingShellError.message
        : "Couldn't load the roadmap.";

    return (
      <div className={pageClassName}>
        <Box padding="32">
          <Title size={24}>Roadmap</Title>
          <Text color="subtle">{errorMessage}</Text>
        </Box>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className={pageClassName}>
          <div className={styles.main}>
            <div className={styles.content}>
              <Box padding="32" display="flex" justifyContent="center">
                <Text color="subtle">Something went wrong in Roadmap.</Text>
              </Box>
            </div>
          </div>
        </div>
      }
    >
      <div ref={pageFadeInRef} className={pageClassName}>
        {embedded ? null : (
          <RoadmapScopeSidebar
            collapsed={scopeSidebarCollapsed}
            scopes={navigation?.scopes ?? []}
            selectedAudience={audience}
            selectedScopeKey={selectedScopeKey}
            selectedPersonLdap={audience === 'person' ? requestedPerson : null}
            groupHierarchy={facetsQuery.data?.group_hierarchy ?? []}
            selectedGroupTeamId={selectedGroupTeam?.team_id ?? null}
            onToggle={() => setScopeSidebarCollapsed((collapsed) => !collapsed)}
            onSelectMyRoadmap={handleSelectMyRoadmap}
            onSelectPerson={handleSelectPerson}
            onSelectTeam={handleSelectGroup}
            onSelectCompany={handleSelectCompany}
            onSelectScope={handleSelectScope}
          />
        )}
        <div className={styles.main}>
          <div className={styles.header} data-testid="roadmap-header">
            <div className={styles.headerTopRow}>
              {isProductRoadmapEnabled ? (
                <RoadmapViewTitle selectedView="engineering" />
              ) : (
                <Title size={18}>{selectedScopeTitle}</Title>
              )}

              <div className={styles.headerControls}>
                <RoadmapQuarterControl
                  availableQuarters={availableQuarters}
                  selectedQuarter={selectedQuarter}
                  onSelect={handleSelectQuarter}
                />
              </div>
            </div>
          </div>

          <div className={styles.tabs}>
            <GroupTabs
              tabs={topTabs}
              activeTab={activeTab}
              onTabClick={(tabId) => updateParams({tab: tabId as RoadmapTab})}
              totalCount={topTabs.reduce((sum, tab) => sum + tab.count, 0)}
              showAllTab={false}
              showCounts={false}
            />
          </div>

          <div
            ref={contentRef}
            className={cx(styles.content, {
              [styles.auditContent]: activeTab === 'audit',
            })}
          >
            {showActiveContentLoading ? (
              <RoadmapContentLoadingSkeleton
                variant={activeTab === 'rollup' ? 'rollup' : 'default'}
              />
            ) : null}
            {!showActiveContentLoading && activeContentError ? (
              <Box padding="24" role="alert">
                <Text color="subtle">
                  {activeContentError instanceof Error
                    ? activeContentError.message
                    : "Couldn't load this Roadmap view."}
                </Text>
              </Box>
            ) : null}

            {canRenderActiveContent && activeTab === 'launches' ? (
              <OverviewTab
                launches={launches}
                launchColumns={launchColumns}
                renderLaunchRow={(row, showOverdue) => (
                  <DashboardLaunchRow key={row.id} row={row} showOverdue={showOverdue} />
                )}
              />
            ) : null}

            {canRenderActiveContent && activeTab === 'all' ? (
              <AllTab data={peopleDashboardData} onNavigate={(tab) => updateParams({tab})} />
            ) : null}

            {canRenderActiveContent && activeTab === 'roadmap' ? (
              <RoadmapTabContent
                progressStrip={
                  <DashboardProgressStrip
                    dashboardProgress={dashboardProgress}
                    onDashboardBucketClick={handleDashboardBucketClick}
                    selectedBucket={selectedDashboardBucket}
                  />
                }
                toolbar={
                  <RoadmapSettingsMenu
                    statusOptions={statusOptions}
                    selectedStatuses={selectedStatuses}
                    onToggleStatus={toggleRoadmapStatus}
                    onClearStatusFilters={clearRoadmapStatusFilters}
                    viewMode={viewMode}
                    onViewModeChange={(nextViewMode) => updateParams({view: nextViewMode})}
                    sortOptions={ROADMAP_SORT_OPTIONS}
                    sortColumn={sortOrder}
                    sortDirection={roadmapSortDirection}
                    onSortChange={({sortColumn, sortDirection}) => {
                      setSortOrder(sortColumn);
                      setRoadmapSortDirection(sortDirection);
                    }}
                    groupingOptions={roadmapGroupingOptions}
                    selectedGrouping={effectiveRoadmapGrouping}
                    onGroupingChange={setRoadmapGrouping}
                  />
                }
                toolbarLeading={
                  viewMode === 'gantt' ? (
                    <div className={cx(styles.ganttLegend, styles.ganttToolbarLegend)}>
                      <div className={styles.ganttLegendItems}>
                        <span className={styles.ganttLegendItem}>
                          <span
                            className={styles.ganttLegendSwatch}
                            style={{backgroundColor: getGanttToneColor('done')}}
                          />
                          Done
                        </span>
                        <span className={styles.ganttLegendItem}>
                          <span
                            className={styles.ganttLegendSwatch}
                            style={{backgroundColor: getGanttToneColor('onTrack')}}
                          />
                          In Progress
                        </span>
                        <span className={styles.ganttLegendItem}>
                          <span
                            className={styles.ganttLegendSwatch}
                            style={{backgroundColor: getGanttToneColor('atRisk')}}
                          />
                          At Risk / Delayed / Blocked / On Hold
                        </span>
                        <span className={styles.ganttLegendItem}>
                          <span
                            className={styles.ganttLegendSwatch}
                            style={{backgroundColor: getGanttToneColor('overdue')}}
                          />
                          Overdue
                        </span>
                        <span className={styles.ganttLegendItem}>
                          <span
                            className={styles.ganttLegendSwatch}
                            style={{backgroundColor: getGanttToneColor('notStarted')}}
                          />
                          Backlog
                        </span>
                      </div>
                    </div>
                  ) : null
                }
                hasResults={roadmapInitiatives.length > 0}
                viewMode={viewMode}
                tableContent={
                  <Table<RoadmapItem>
                    columns={roadmapColumns}
                    data={roadmapInitiatives}
                    border={false}
                    headerTextSize="small"
                    columnLabelOverrides={{
                      initiative: 'Initiative',
                      start_date: 'Start Date',
                      end_date: 'End Date',
                      owner: 'DRI',
                      status: 'Status',
                      links: 'Resources',
                    }}
                    className={cx(styles.table, styles.roadmapTable, styles.stickyTable)}
                  >
                    <Table.Header data-testid="roadmap-initiatives-table-header" />
                    <Table.Body className={styles.tableBody}>
                      {roadmapTree.length === 0 ? (
                        <Table.Row>
                          <Table.Cell colSpan={roadmapColumns.length}>
                            <Box padding="24" display="flex" justifyContent="center">
                              <Text color="subtle">
                                No initiatives matched the current roadmap filters.
                              </Text>
                            </Box>
                          </Table.Cell>
                        </Table.Row>
                      ) : (
                        roadmapTree.map((node) => (
                          <RoadmapHierarchyRows
                            key={node.key}
                            node={node}
                            depth={0}
                            collapsedNodes={collapsedNodes}
                            onToggleNode={toggleNode}
                            roadmapNodeUpdatesByWorkstreamId={roadmapNodeUpdatesByWorkstreamId}
                          />
                        ))
                      )}
                    </Table.Body>
                  </Table>
                }
                ganttContent={
                  <div className={styles.ganttBoard}>
                    <div className={styles.ganttScrollContent}>
                      {roadmapTree.map((node) => (
                        <RoadmapGanttSection
                          key={node.key}
                          node={node}
                          depth={0}
                          collapsedNodes={collapsedNodes}
                          onToggleNode={toggleNode}
                          roadmapNodeUpdatesByWorkstreamId={roadmapNodeUpdatesByWorkstreamId}
                          months={ganttTimeline.months}
                          weeks={ganttTimeline.weeks}
                          totalDays={ganttTimeline.totalDays}
                          quarterStart={currentQuarterWindow.start}
                          quarterEnd={currentQuarterWindow.end}
                          today={ganttToday}
                        />
                      ))}
                    </div>
                  </div>
                }
              />
            ) : null}

            {canRenderActiveContent && activeTab === 'dependencies' && selectedTimeframeKey ? (
              <Suspense fallback={<RoadmapContentLoadingSkeleton variant="dependencies" />}>
                <DependenciesTab
                  timeframe={selectedTimeframeKey}
                  scopeKey={selectedScopeKey}
                  groupIds={selectedGroupIds}
                  quarter={selectedQuarter}
                  participantEmployeeId={
                    audience === 'my'
                      ? loggedInEmployee.user_id
                      : audience === 'person'
                        ? selectedPerson?.user_id
                        : null
                  }
                  participantScopeEmployeeId={
                    audience === 'person' ? selectedPerson?.user_id : null
                  }
                />
              </Suspense>
            ) : null}

            {canRenderActiveContent && activeTab === 'rollup' && displayedRollup ? (
              <RollupTab
                rollup={displayedRollup}
                scopeLabel={audience === 'scope' ? navigation?.scope?.label : null}
                summaryLabel={
                  audience === 'my'
                    ? 'My roadmap'
                    : audience === 'person'
                      ? (selectedPerson?.name ?? requestedPerson ?? 'Person')
                      : audience === 'group'
                        ? (selectedGroupTeam?.team_name ?? 'Group')
                        : 'Company'
                }
              />
            ) : null}

            {canRenderActiveContent && activeTab === 'allocations' ? (
              <section className={styles.section}>
                <AuditSection
                  title="Headcount"
                  action={
                    <Button
                      variant="outline"
                      size="small"
                      withIconStart={<UIIcon src={DownloadLine} />}
                      onClick={handleExportHeadcount}
                      disabled={flatAuditWorkstreamRows.length === 0}
                    >
                      Export
                    </Button>
                  }
                >
                  <div className={cx(styles.tableShell, styles.auditTableShell)}>
                    <Table<AuditWorkstreamNode>
                      columns={allocationColumns}
                      data={flatAuditWorkstreamRows}
                      headerTextSize="small"
                      resizable={false}
                      columnLabelOverrides={{
                        workstream: 'Workstream',
                        dev_weeks: 'Dev Weeks',
                        total_est_hc: 'Total Est HC',
                        allocation_percent: 'Allocation',
                      }}
                      className={styles.table}
                    >
                      <Table.Header />
                      <Table.Body className={cx(styles.tableBody, styles.tableBodyNoStableGutter)}>
                        {flatAuditWorkstreamRows.length === 0 ? (
                          <Table.Row>
                            <Table.Cell colSpan={allocationColumns.length}>
                              <Box padding="24" display="flex" justifyContent="center">
                                <Text color="subtle">
                                  No workstreams found for the selected filters.
                                </Text>
                              </Box>
                            </Table.Cell>
                          </Table.Row>
                        ) : (
                          flatAuditWorkstreamRows.map((node) => (
                            <AuditWorkstreamRow
                              key={node.key}
                              node={node}
                              totalHc={auditWorkstreamAllocation.totalHc}
                            />
                          ))
                        )}
                        <Table.Row className={styles.auditTotalRow}>
                          <Table.Cell>
                            <Text isBold size="small">
                              Total
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="small" className={styles.auditMetricText}>
                              {formatDecimal(auditWorkstreamAllocation.totalDevWeeks)}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="small" className={styles.auditMetricText}>
                              {formatDecimal(auditWorkstreamAllocation.totalHc)}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="small" className={styles.auditMetricText}>
                              100%
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </div>
                </AuditSection>

                <AuditSection title="Investment Category">
                  <div className={cx(styles.tableShell, styles.auditTableShell)}>
                    <Table<AuditInvestmentNode>
                      columns={investmentColumns}
                      data={flatAuditInvestmentRows}
                      headerTextSize="small"
                      resizable={false}
                      columnLabelOverrides={{
                        workstream: 'Workstream',
                        total: 'Total',
                        ...investmentCategoryData.activeCategories.reduce<Record<string, string>>(
                          (accumulator, category) => ({
                            ...accumulator,
                            [`investment_${category}`]:
                              INVESTMENT_CATEGORY_SHORT_LABELS[category] ?? category,
                          }),
                          {}
                        ),
                      }}
                      className={styles.table}
                    >
                      <Table.Header />
                      <Table.Body className={cx(styles.tableBody, styles.tableBodyNoStableGutter)}>
                        {flatAuditInvestmentRows.length === 0 ? (
                          <Table.Row>
                            <Table.Cell
                              colSpan={investmentCategoryData.activeCategories.length + 2}
                            >
                              <Box padding="24" display="flex" justifyContent="center">
                                <Text color="subtle">No investment category allocation found.</Text>
                              </Box>
                            </Table.Cell>
                          </Table.Row>
                        ) : (
                          flatAuditInvestmentRows.map((node) => (
                            <AuditInvestmentRow
                              key={node.key}
                              node={node}
                              activeCategories={investmentCategoryData.activeCategories}
                            />
                          ))
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </AuditSection>
              </section>
            ) : null}

            {canRenderActiveContent && activeTab === 'audit' ? (
              <AuditFindingsTab
                state={
                  !auditFindingsQuery.isEnabled
                    ? 'idle'
                    : auditFindingsQuery.isError
                      ? 'error'
                      : auditFindingsQuery.data
                        ? 'data'
                        : auditFindingsQuery.isPending
                          ? 'loading'
                          : 'data'
                }
                data={auditFindingsQuery.data}
                error={auditFindingsQuery.error instanceof Error ? auditFindingsQuery.error : null}
                filters={effectiveAuditFilters}
                expansionKey={auditFindingsQuery.filterKey}
                isLoadingMore={auditFindingsQuery.isFetching && auditFindingsQuery.pageIndex > 0}
                nextCursor={auditFindingsQuery.nextCursor}
                onFiltersChange={handleAuditFiltersChange}
                onLoadMore={auditFindingsQuery.nextPage}
                onResetPagination={auditFindingsQuery.resetPagination}
                onRetry={() => void auditFindingsQuery.refetch()}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Sentry.ErrorBoundary>
  );
};
